import { localEventDate } from '../../shared/eventTimezones';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { supabase } from '../config/supabase';
import { lookupLabel } from '../data/lookups';
import { downloadBlob } from '../services/zipExport';
import type { LookupState, SortDirection, TimeFormat, UserPreferences } from '../types';
import { comparePlayerNames, formatPlayerName } from '../utils/playerPreferences';
import { loadParticipationReportRows, type ParticipationReportRow } from '../utils/participationReport';
import { authoritativePlayedEvents, effectiveHistoricalLineup, loadHistoricalPlayedEvents } from '../utils/historicalLineup';
import { lookupTeamAccentColor } from '../utils/teamColors';
import { formatTime } from '../utils/timeFormat';
import { isPlayedLineResult, scoreLineResult, type LineResultValue } from '../utils/resultScoring';
import { calculateLineupAverageEdr, formatEdr } from '../utils/edr';
import { resultParticipantsByLine } from '../utils/resultReporting';
import { compatibilityChipColor, compatibilitySortRank, normalizePairingRule, pairingRuleLabels, type PartnerCompatibility, type PartnerPairingRule } from '../utils/partnerPreferences';
import { PlayerDisplayControls } from './PlayerDisplayControls';
import { SeasonTeamMultiSelect } from './SeasonTeamMultiSelect';
import { UserLoginActivityReport } from './UserLoginActivityReport';
import { useAuth } from '../auth/AuthContext';
import { useAuditUserId } from '../auth/AuthContext';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import { restoreAccessibleTeamSelection, sameTeamSelection, shouldShowCaptainScopeEmpty } from '../utils/captainWorkflowScope';

type ReportsProps = {
  lookups: LookupState;
  defaultCaptainTeamIds: string[];
  userPreferences: UserPreferences;
  onUserPreferencesChange: (patch: Partial<UserPreferences>) => void;
  timeFormat: TimeFormat;
  onOpenParticipationEventsForPlayer?: (playerId: string) => void;
  initialPlayerSchedulePlayerId?: string | null;
  initialReportType?: ReportType | null;
  initialParticipationTeamId?: string | null;
  canViewPartnerPreferences?: boolean;
  readablePartnerPreferenceTeamIds?: string[];
};

type MatchRow = {
  id: string;
  team_id: string;
  league_configuration_id: string;
  opponent_name: string | null;
  match_date: string;
  match_time: string | null;
  warmup_time?: string | null;
  facility_id: string | null;
  opponent_average_edr: number | null;
  match_site_type: string | null;
  match_status: string;
  lineup_status: string;
};

type LineupEntryRow = {
  match_event_id: string;
  league_configuration_position_id: string;
  slot_number: number;
  player_id: string;
};

type StandbyRow = {
  match_event_id: string;
  player_id: string;
  notes: string | null;
};

type ParticipationEventRow = {
  match_event_id: string;
  league_configuration_position_id: string | null;
};

type PositionRow = {
  id: string;
  league_configuration_id?: string | null;
  position_code: string;
  line_type: string;
  display_order?: number | null;
};

type PlayerRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  sex?: string | null;
  estimated_dynamic_rating?: number | null;
};

type LineResultRow = {
  match_event_id: string;
  league_configuration_position_id: string;
  line_result: string;
  result_value?: LineResultValue;
  set_scores: string | null;
};

type LineStatusRow = {
  match_event_id: string;
  league_configuration_position_id: string;
  line_status: 'normal' | 'default_by_opponent' | 'default_by_us';
};

type TeamResultRow = {
  id: string;
  team_id: string;
  opponent_name: string | null;
  match_date: string;
  match_time: string | null;
  facility_id: string | null;
  opponent_average_edr: number | null;
  match_status: string;
  match_results?: {
    result_status: string;
    our_team_score: number;
    opponent_team_score: number;
    team_result_value: string;
  }[] | null;
};

type ScheduleItem = {
  match: MatchRow;
  position?: PositionRow;
  lineupEntries: LineupEntryRow[];
  isStandby: boolean;
  lineStatus?: LineStatusRow['line_status'];
  lineResult?: LineResultRow;
};

type ReportType = 'player_schedule' | 'player_participation' | 'player_results' | 'partner_results' | 'partner_compatibility' | 'team_schedule' | 'team_contacts' | 'team_results' | 'edr_movement' | 'user_login_activity';
type ReportsViewState = {
  reportType: ReportType;
  playerId: string;
  teamIds: string[];
  participationTeamIds: string[];
  teamResultsTeamIds: string[];
  resultPlayerId: string;
  resultTeamIds: string[];
  partnerTeamIds: string[];
  partnerPlayerId: string;
  compatibilityTeamIds: string[];
  teamScheduleTeamIds: string[];
  contactTeamIds: string[];
  edrTeamIds: string[];
  explicitTeamReports?: ReportType[];
  participationSort: {
    field: ParticipationSortField;
    direction: SortDirection;
  };
};

type ResultsReportMatch = MatchRow & {
  result_status: string;
  our_team_score: number;
  opponent_team_score: number;
  team_result_value: string;
  organization_id: string | null;
};

type PlayerResultDetail = {
  id: string;
  match: ResultsReportMatch;
  position: PositionRow;
  partners: string[];
  lineResult: LineResultValue;
};

type PlayerResultsSummary = {
  wins: number;
  losses: number;
  singlesWins: number;
  singlesLosses: number;
  doublesWins: number;
  doublesLosses: number;
  mixedWins: number;
  mixedLosses: number;
  homeWins: number;
  homeLosses: number;
  awayWins: number;
  awayLosses: number;
  neutralWins: number;
  neutralLosses: number;
  lateCancels: number;
  lateSubstitutes: number;
};

type PartnerResultDetail = {
  id: string;
  player1Id: string;
  player2Id: string;
  match: ResultsReportMatch;
  position: PositionRow;
  lineResult: LineResultValue;
};

type PartnerSummaryRow = {
  key: string;
  player1Id: string;
  player2Id: string;
  player1Sex: string | null;
  player2Sex: string | null;
  teams: string[];
  details: PartnerResultDetail[];
  wins: number;
  losses: number;
  homeWins: number;
  homeLosses: number;
  awayWins: number;
  awayLosses: number;
  lastPlayed: string;
};

type PartnerCompatibilityRow = {
  id: string;
  workspace_id: string;
  team_id: string | null;
  player1_id: string;
  player2_id: string;
  compatibility: PartnerCompatibility | null;
  pairing_rule: PartnerPairingRule | null;
  is_active?: boolean | null;
  notes: string | null;
};

type ResultSortField = 'match_date' | 'team' | 'opponent' | 'position' | 'line_result' | 'team_result' | 'facility';
type PartnerSortField = 'player1' | 'player2' | 'matches' | 'record' | 'win_percentage' | 'last_played';
type PartnerCompatibilitySortField = 'player1' | 'player2' | 'compatibility' | 'notes';
type ContactSortField = 'name' | 'city' | 'state';

type ContactReportRow = {
  key: string;
  playerId: string;
  teamId: string;
  first_name: string | null;
  last_name: string | null;
  sex: string | null;
  mobile_phone: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
};

type TeamResultDetailRow = ResultsReportMatch & {
  lineup_average_edr: number | null;
  lines: {
    position: PositionRow;
    result: LineResultValue;
    setScores?: string | null;
  }[];
};

type TeamScheduleLineRow = {
  position: PositionRow;
  players: string[];
  lineStatus?: LineStatusRow['line_status'];
  result?: LineResultRow | null;
};

type TeamScheduleRow = ResultsReportMatch & {
  warmup_time: string | null;
  lines: TeamScheduleLineRow[];
};

type TeamSchedulePlayerSummaryRow = {
  playerId: string;
  playerName: string;
  teamNames: string[];
  scheduled: number;
  played: number;
};

type EdrTeamRow = {
  id: string;
  team_name: string;
  workspace_id: string;
  season_label: string | null;
};

type TeamPlayingDateRange = {
  team_id: string;
  first_match_date: string | null;
  last_match_date: string | null;
};

type EdrRosterRow = {
  team_id: string;
  player_id: string;
  players?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    sex: string | null;
    estimated_dynamic_rating: number | null;
    tennis_record_profile_url?: string | null;
  } | null;
};

type EdrSyncRunRow = {
  player_id: string | null;
  started_at: string;
  completed_at: string | null;
  observed_value: number | null;
  prior_value: number | null;
  changed: boolean;
  status: string;
};

type EdrSnapshotRow = {
  id?: string;
  workspace_id: string;
  team_id: string;
  player_id: string;
  snapshot_type: string;
  snapshot_label: string | null;
  baseline_edr: number | null;
  captured_at: string;
  source: string;
  tennis_record_profile_url: string | null;
};

type EdrSnapshotUpsertRow = Omit<EdrSnapshotRow, 'id'>;

type EdrMovementPlayerRow = {
  key: string;
  teamId: string;
  teamName: string;
  seasonLabel: string;
  playerId: string;
  playerName: string;
  sex: string | null;
  startEdr: number | null;
  currentEdr: number | null;
  delta: number | null;
  startSource: string;
  startDate: string | null;
  currentDate: string | null;
  observations: EdrSyncRunRow[];
};

type EdrTeamSummaryRow = {
  teamId: string;
  teamName: string;
  seasonLabel: string;
  players: number;
  ratedStart: number;
  ratedCurrent: number;
  averageStart: number | null;
  averageCurrent: number | null;
  averageDelta: number | null;
};

type ParticipationSortField =
  | 'playerName'
  | 'targetMatches'
  | 'adjustedTarget'
  | 'totalPlayed'
  | 'lateCancels'
  | 'lateSubstitutes'
  | 'totalFuture'
  | 'statusDelta';

function formatDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function resultLabel(value: string): string {
  if (value === 'Not Entered') return 'Not Entered';
  if (value === 'Default by Opponent') return 'Default by Opponent';
  if (value === 'Default by Us') return 'Default by Us';
  if (value === 'Retired Win') return 'Retired Win';
  if (value === 'Retired Loss') return 'Retired Loss';
  if (value === 'Double Default') return 'Double Default';
  if (value === 'win') return 'Won';
  if (value === 'loss') return 'Lost';
  if (value === 'tie') return 'Tied';
  if (value === 'unfinished') return 'Unfinished';
  if (value === 'defaulted') return 'Defaulted';
  return value;
}

function recordText(wins: number, losses: number): string {
  return `${wins}-${losses}`;
}

function winPercentage(wins: number, losses: number): string {
  const total = wins + losses;
  return total > 0 ? `${Math.round((wins / total) * 100)}%` : '-';
}

function numericEdr(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Number(numeric.toFixed(2)) : null;
}

function averageRating(values: Array<number | null | undefined>): number | null {
  const numericValues = values.map(numericEdr).filter((value): value is number => value !== null);
  if (numericValues.length === 0) return null;
  return Number((numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length).toFixed(2));
}

function edrDelta(start: number | null, end: number | null): number | null {
  if (start === null || end === null) return null;
  return Number((end - start).toFixed(2));
}

function formatDelta(value: number | null): string {
  if (value === null) return '-';
  if (value > 0) return `+${value.toFixed(2)}`;
  return value.toFixed(2);
}

function dateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function countResult(summary: { wins: number; losses: number }, result: LineResultValue): void {
  const score = scoreLineResult(result);
  if (score === 1) summary.wins += 1;
  if (score === -1) summary.losses += 1;
}

function resultSortValue(row: PlayerResultDetail, field: ResultSortField, lookups: LookupState): string {
  if (field === 'match_date') return row.match.match_date;
  if (field === 'team') return lookupLabel(lookups, 'teams', row.match.team_id);
  if (field === 'opponent') return row.match.opponent_name || '';
  if (field === 'position') return row.position.position_code;
  if (field === 'line_result') return row.lineResult;
  if (field === 'team_result') return row.match.team_result_value;
  return lookupLabel(lookups, 'facilities', row.match.facility_id);
}

function teamResultFromScores(match: ResultsReportMatch): string {
  if (match.team_result_value && match.team_result_value !== 'Unknown') return match.team_result_value;
  if (match.our_team_score > match.opponent_team_score) return 'Win';
  if (match.our_team_score < match.opponent_team_score) return 'Loss';
  if (match.our_team_score === match.opponent_team_score && match.result_status !== 'Not Started') return 'Tie';
  return 'Unknown';
}

function lineTypeLabel(position?: PositionRow): string {
  if (!position) return '';
  if (position.line_type === 'singles') return 'Singles';
  if (position.line_type === 'doubles') return 'Doubles';
  if (position.line_type === 'mixed_doubles') return 'Mixed Doubles';
  return position.line_type || '';
}

function matchHeading(match: MatchRow, lookups: LookupState, timeFormat: TimeFormat): string {
  const date = formatDate(localEventDate(match));
  const time = formatTime(match.match_time, timeFormat, match);
  return `${date}${time ? ` ${time}` : ''} - ${lookupLabel(lookups, 'teams', match.team_id)} vs ${match.opponent_name || 'TBD'}`;
}

function matchDetailLine(match: MatchRow, lookups: LookupState): string {
  const facility = lookupLabel(lookups, 'facilities', match.facility_id) || 'No facility';
  return `${facility} • ${match.match_site_type || 'Unknown'} • Match Status: ${match.match_status} • Lineup Status: ${match.lineup_status || 'Not Started'}`;
}

function itemLine(item: ScheduleItem, playersById: Record<string, PlayerRow>, preferences: UserPreferences): string {
  if (item.isStandby) return 'Standby';
  if (item.lineStatus === 'default_by_opponent') return 'Default by Opponent';
  if (item.lineStatus === 'default_by_us') return 'Default by Us';
  const position = item.position?.position_code || 'Line';
  const names = item.lineupEntries
    .sort((a, b) => a.slot_number - b.slot_number)
    .map((entry) => formatPlayerName(playersById[entry.player_id], preferences) || 'Unknown Player')
    .join(' / ');
  return `${position} - ${names || 'TBD'}`;
}

function lineStatusLabel(status?: LineStatusRow['line_status']): string {
  if (status === 'default_by_opponent') return 'Default by Opponent';
  if (status === 'default_by_us') return 'Default by Us';
  return 'Normal';
}

function teamScheduleLineDisplay(line: TeamScheduleLineRow, completed: boolean): string {
  const lineup = line.lineStatus && line.lineStatus !== 'normal'
    ? lineStatusLabel(line.lineStatus)
    : line.players.length > 0
      ? line.players.join(' / ')
      : 'TBD';
  if (!completed || !line.result) return lineup;
  const rawResult = line.result.result_value || line.result.line_result;
  const result = rawResult ? resultLabel(rawResult) : '';
  return [lineup, result, line.result.set_scores || ''].filter(Boolean).join(' - ');
}

function compactLineupStatus(status: string): string {
  return ['Draft', 'Published'].includes(status) ? status : '';
}

function compactScheduleBlock(item: ScheduleItem, playersById: Record<string, PlayerRow>, lookups: LookupState, preferences: UserPreferences, timeFormat: TimeFormat): string {
  const date = formatDate(localEventDate(item.match));
  const time = formatTime(item.match.match_time, timeFormat, item.match);
  const lines = [
    `${date}${time ? ` ${time}` : ''}`,
    `${lookupLabel(lookups, 'teams', item.match.team_id)} vs ${item.match.opponent_name || 'TBD'}`,
    lookupLabel(lookups, 'facilities', item.match.facility_id) || '',
    itemLine(item, playersById, preferences),
    compactLineupStatus(item.match.lineup_status),
  ].filter((line) => line.trim().length > 0);
  return lines.join('\n');
}

function copyTextForReport(playerName: string, completed: ScheduleItem[], future: ScheduleItem[], playersById: Record<string, PlayerRow>, lookups: LookupState, preferences: UserPreferences, timeFormat: TimeFormat): string {
  const blocks = [...future, ...completed].map((item) => compactScheduleBlock(item, playersById, lookups, preferences, timeFormat));
  return [playerName, ...blocks].join('\n\n');
}

function compareScheduleItemsAscending(a: ScheduleItem, b: ScheduleItem): number {
  return a.match.match_date.localeCompare(b.match.match_date) || (a.match.match_time || '').localeCompare(b.match.match_time || '');
}

function statusColor(statusDelta: number): 'primary' | 'success' | 'error' {
  if (statusDelta < 0) return 'error';
  if (statusDelta > 0) return 'primary';
  return 'success';
}

function statusLabel(statusDelta: number): string {
  if (statusDelta < 0) return `Low (${statusDelta})`;
  if (statusDelta > 0) return `High (+${statusDelta})`;
  return 'On Target';
}

function compareParticipationRows(a: ParticipationReportRow, b: ParticipationReportRow, field: ParticipationSortField, direction: SortDirection): number {
  const multiplier = direction === 'asc' ? 1 : -1;
  const aValue = a[field];
  const bValue = b[field];
  const result = typeof aValue === 'number' && typeof bValue === 'number'
    ? aValue - bValue
    : String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
  return result * multiplier;
}

function copyParticipationText(teamName: string, rows: ParticipationReportRow[]): string {
  const header = 'Player                 OrigTgt AdjTgt LateCancel LateSub Played          Future          Status';
  const body = rows.map((row) => {
    const player = row.playerName.padEnd(22).slice(0, 22);
    return [
      player,
      String(row.targetMatches).padStart(6),
      String(row.adjustedTarget).padStart(6),
      String(row.lateCancels).padStart(10),
      String(row.lateSubstitutes).padStart(7),
      `${row.totalPlayed} (S:${row.singlesPlayed} / D:${row.doublesPlayed})`.padStart(15),
      `${row.totalFuture} (S:${row.futureSingles} / D:${row.futureDoubles})`.padStart(15),
      statusLabel(row.statusDelta).padStart(12),
    ].join(' ');
  });
  return [`Team: ${teamName}`, '', header, '', ...body].join('\n');
}

function csvEscape(value: unknown): string {
  const text = value == null ? '' : String(value);
  if (!/[",\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function compactFilePart(value: string): string {
  return value.replace(/[^A-Za-z0-9]+/g, '');
}

function reportTypeLabel(reportType: ReportType): string {
  if (reportType === 'player_schedule') return 'Player Schedule Report';
  if (reportType === 'player_participation') return 'Player Participation Report';
  if (reportType === 'player_results') return 'Player Results Report';
  if (reportType === 'partner_results') return 'Partner Results Report';
  if (reportType === 'partner_compatibility') return 'Partner Compatibility Report';
  if (reportType === 'team_schedule') return 'Team Schedule Report';
  if (reportType === 'team_contacts') return 'Team Contact Report';
  if (reportType === 'team_results') return 'Team Results Report';
  if (reportType === 'user_login_activity') return 'User Login Activity Report';
  return 'EDR Movement Report';
}

function reportPdfFilename(title: string, context: string): string {
  const timestamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
  const compact = ['CourtCaptain', title, context, timestamp]
    .filter(Boolean)
    .join(' ')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `${compact}.pdf`;
}

function normalizeReportPdfText(value: string): string {
  return value
    .replace(/\u2022/g, '-')
    .replace(/\u2192/g, '->')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\u00a0/g, ' ')
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '');
}

export function Reports({ lookups, defaultCaptainTeamIds, userPreferences, onUserPreferencesChange, timeFormat, onOpenParticipationEventsForPlayer, initialPlayerSchedulePlayerId, initialReportType, initialParticipationTeamId, canViewPartnerPreferences = true, readablePartnerPreferenceTeamIds = [] }: ReportsProps) {
  const auditUserId = useAuditUserId();
  const { isOwner, appUser } = useAuth();
  const canViewLoginActivity = isOwner || appUser?.role === 'admin';
  const defaultViewState = useMemo<ReportsViewState>(() => ({
    reportType: 'player_schedule',
    playerId: '',
    teamIds: defaultCaptainTeamIds,
    participationTeamIds: defaultCaptainTeamIds,
    teamResultsTeamIds: defaultCaptainTeamIds,
    resultPlayerId: '',
    resultTeamIds: defaultCaptainTeamIds,
    partnerTeamIds: defaultCaptainTeamIds,
    partnerPlayerId: '',
    compatibilityTeamIds: defaultCaptainTeamIds,
    teamScheduleTeamIds: defaultCaptainTeamIds,
    contactTeamIds: defaultCaptainTeamIds,
    edrTeamIds: defaultCaptainTeamIds,
    explicitTeamReports: [],
    participationSort: { field: 'statusDelta', direction: 'asc' },
  }), [defaultCaptainTeamIds]);
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState<ReportsViewState>({
    userId: auditUserId,
    routeKey: 'reports',
    defaultState: defaultViewState,
  });
  const [reportType, setReportType] = useState<ReportType>(initialReportType || savedViewState.reportType);
  const [playerId, setPlayerId] = useState(initialPlayerSchedulePlayerId || savedViewState.playerId);
  const [teamIds, setTeamIds] = useState<string[]>(savedViewState.teamIds);
  const [explicitTeamReports, setExplicitTeamReports] = useState<ReportType[]>(savedViewState.explicitTeamReports ?? ['player_schedule', 'player_participation', 'player_results', 'partner_results', 'partner_compatibility', 'team_schedule', 'team_contacts', 'team_results', 'edr_movement']);
  const markReportTeamSelection = (type: ReportType) => setExplicitTeamReports((current) => current.includes(type) ? current : [...current, type]);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [includeFuture, setIncludeFuture] = useState(true);
  const [completedItems, setCompletedItems] = useState<ScheduleItem[]>([]);
  const [futureItems, setFutureItems] = useState<ScheduleItem[]>([]);
  const [playersById, setPlayersById] = useState<Record<string, PlayerRow>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);
  const [manualCopyText, setManualCopyText] = useState('');
  const [participationRows, setParticipationRows] = useState<ParticipationReportRow[]>([]);
  const [participationTeamIds, setParticipationTeamIds] = useState<string[]>(initialParticipationTeamId ? [initialParticipationTeamId] : savedViewState.participationTeamIds);
  const [includeInactivePlayers, setIncludeInactivePlayers] = useState(false);
  const [includeSubstitutePlayers, setIncludeSubstitutePlayers] = useState(false);
  const [teamResultsTeamIds, setTeamResultsTeamIds] = useState<string[]>(savedViewState.teamResultsTeamIds);
  const [teamResultsMatchStatus, setTeamResultsMatchStatus] = useState('Completed');
  const [teamResultsResultStatus, setTeamResultsResultStatus] = useState('');
  const [teamResultsDateFrom, setTeamResultsDateFrom] = useState('');
  const [teamResultsDateTo, setTeamResultsDateTo] = useState('');
  const [teamResultsSiteType, setTeamResultsSiteType] = useState('');
  const [teamResultsResultValue, setTeamResultsResultValue] = useState('');
  const [teamResultRows, setTeamResultRows] = useState<TeamResultDetailRow[]>([]);
  const [edrTeamIds, setEdrTeamIds] = useState<string[]>(savedViewState.edrTeamIds);
  const [edrPlayerRows, setEdrPlayerRows] = useState<EdrMovementPlayerRow[]>([]);
  const [edrTeamRows, setEdrTeamRows] = useState<EdrTeamSummaryRow[]>([]);
  const [edrObservationRows, setEdrObservationRows] = useState<Array<EdrMovementPlayerRow & { observation: EdrSyncRunRow; observedDelta: number | null }>>([]);
  const [resultPlayerId, setResultPlayerId] = useState(savedViewState.resultPlayerId);
  const [resultTeamIds, setResultTeamIds] = useState<string[]>(savedViewState.resultTeamIds);
  const [resultDateFrom, setResultDateFrom] = useState('');
  const [resultDateTo, setResultDateTo] = useState('');
  const [resultLineType, setResultLineType] = useState('');
  const [resultSiteType, setResultSiteType] = useState('');
  const [playerResultRows, setPlayerResultRows] = useState<PlayerResultDetail[]>([]);
  const [playerResultSummary, setPlayerResultSummary] = useState<PlayerResultsSummary>({ wins: 0, losses: 0, singlesWins: 0, singlesLosses: 0, doublesWins: 0, doublesLosses: 0, mixedWins: 0, mixedLosses: 0, homeWins: 0, homeLosses: 0, awayWins: 0, awayLosses: 0, neutralWins: 0, neutralLosses: 0, lateCancels: 0, lateSubstitutes: 0 });
  const [resultSort, setResultSort] = useState<{ field: ResultSortField; direction: SortDirection }>({ field: 'match_date', direction: 'desc' });
  const [partnerTeamIds, setPartnerTeamIds] = useState<string[]>(savedViewState.partnerTeamIds);
  const [partnerPlayerId, setPartnerPlayerId] = useState(savedViewState.partnerPlayerId);
  const [partnerDateFrom, setPartnerDateFrom] = useState('');
  const [partnerDateTo, setPartnerDateTo] = useState('');
  const [partnerSiteType, setPartnerSiteType] = useState('');
  const [partnerMinimumMatches, setPartnerMinimumMatches] = useState('1');
  const [partnerRows, setPartnerRows] = useState<PartnerSummaryRow[]>([]);
  const [partnerSort, setPartnerSort] = useState<{ field: PartnerSortField; direction: SortDirection }>({ field: 'matches', direction: 'desc' });
  const [selectedPartnerRow, setSelectedPartnerRow] = useState<PartnerSummaryRow | null>(null);
  const [compatibilityTeamIds, setCompatibilityTeamIds] = useState<string[]>(savedViewState.compatibilityTeamIds);
  const [compatibilityRows, setCompatibilityRows] = useState<PartnerCompatibilityRow[]>([]);
  const [compatibilitySort, setCompatibilitySort] = useState<{ field: PartnerCompatibilitySortField; direction: SortDirection }>({ field: 'compatibility', direction: 'asc' });
  const [teamScheduleTeamIds, setTeamScheduleTeamIds] = useState<string[]>(savedViewState.teamScheduleTeamIds || []);
  const [teamScheduleDateFrom, setTeamScheduleDateFrom] = useState('');
  const [teamScheduleDateTo, setTeamScheduleDateTo] = useState('');
  const [teamScheduleRows, setTeamScheduleRows] = useState<TeamScheduleRow[]>([]);
  const [teamSchedulePlayerSummaryRows, setTeamSchedulePlayerSummaryRows] = useState<TeamSchedulePlayerSummaryRow[]>([]);
  const [contactTeamIds, setContactTeamIds] = useState<string[]>(savedViewState.contactTeamIds);
  const [contactIncludePhone, setContactIncludePhone] = useState(true);
  const [contactIncludeEmail, setContactIncludeEmail] = useState(true);
  const [contactIncludeCityState, setContactIncludeCityState] = useState(true);
  const [contactDeduplicate, setContactDeduplicate] = useState(true);
  const [contactRows, setContactRows] = useState<ContactReportRow[]>([]);
  const [contactSort, setContactSort] = useState<{ field: ContactSortField; direction: SortDirection }>({ field: 'name', direction: 'asc' });
  const [participationSort, setParticipationSort] = useState<{ field: ParticipationSortField; direction: SortDirection }>(savedViewState.participationSort);
  const [selectedParticipationRow, setSelectedParticipationRow] = useState<ParticipationReportRow | null>(null);
  const [autoLoadedSchedulePlayerId, setAutoLoadedSchedulePlayerId] = useState<string | null>(null);
  const restoredViewStateKey = useRef('');
  const reportContentRef = useRef<HTMLDivElement | null>(null);
  const [viewStateReady, setViewStateReady] = useState(false);

  const validTeamIdSet = useMemo(() => new Set(lookups.teams.map((team) => team.value)), [lookups.teams]);
  const isUnselectedCaptainReport = (type: ReportType, selected: string[]) => shouldShowCaptainScopeEmpty(
    selected, defaultCaptainTeamIds, explicitTeamReports.includes(type) ? 'explicit' : 'default',
  );
  const reportTeamScope: Record<ReportType, string[]> = {
    player_schedule: teamIds,
    player_participation: participationTeamIds,
    player_results: resultTeamIds,
    partner_results: partnerTeamIds,
    partner_compatibility: compatibilityTeamIds,
    team_schedule: teamScheduleTeamIds,
    team_contacts: contactTeamIds,
    team_results: teamResultsTeamIds,
    edr_movement: edrTeamIds,
    user_login_activity: [],
  };
  const showMissingCaptainReportScope = reportType !== 'user_login_activity' && defaultCaptainTeamIds.length === 0
    && reportTeamScope[reportType].length === 0
    && !explicitTeamReports.includes(reportType);
  const validPlayerIdSet = useMemo(() => new Set(lookups.players.map((player) => player.value)), [lookups.players]);
  const selectedPlayerName = useMemo(() => lookupLabel(lookups, 'players', playerId), [lookups, playerId]);
  const resultPlayerName = useMemo(() => lookupLabel(lookups, 'players', resultPlayerId), [lookups, resultPlayerId]);
  const teamSelectionLabel = (ids: string[], emptyLabel: string): string => {
    if (ids.length === 0) return emptyLabel;
    if (ids.length === 1) return lookupLabel(lookups, 'teams', ids[0]);
    return `${ids.length} teams selected`;
  };
  const participationTeamName = useMemo(() => teamSelectionLabel(participationTeamIds, ''), [lookups, participationTeamIds]);
  const teamResultsTeamName = useMemo(() => teamSelectionLabel(teamResultsTeamIds, 'All Teams'), [lookups, teamResultsTeamIds]);
  const edrTeamName = useMemo(() => teamSelectionLabel(edrTeamIds, 'Select one or more teams'), [lookups, edrTeamIds]);
  const teamScheduleTeamName = useMemo(() => teamSelectionLabel(teamScheduleTeamIds, 'Select one or more teams'), [lookups, teamScheduleTeamIds]);
  const filteredContactTeams = lookups.teams;
  const hasMatchGroupSelected = includeCompleted || includeFuture;
  const visibleCompletedItems = includeCompleted ? completedItems : [];
  const visibleFutureItems = includeFuture ? futureItems : [];
  const sortedParticipationRows = useMemo(() => {
    return [...participationRows].sort((a, b) => {
      if (userPreferences.groupPlayersBySex) {
        const sexCompare = String(a.sex || '').localeCompare(String(b.sex || ''), undefined, { sensitivity: 'base' });
        if (sexCompare) return sexCompare;
      }
      const primary = compareParticipationRows(a, b, participationSort.field, participationSort.direction);
      return primary || comparePlayerNames(
        { first_name: a.firstName, last_name: a.lastName, sex: a.sex },
        { first_name: b.firstName, last_name: b.lastName, sex: b.sex },
        userPreferences,
      );
    });
  }, [participationRows, participationSort, userPreferences]);
  const sortedPlayerResultRows = useMemo(() => {
    return [...playerResultRows].sort((a, b) => {
      const aValue = resultSortValue(a, resultSort.field, lookups);
      const bValue = resultSortValue(b, resultSort.field, lookups);
      const result = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
      return (resultSort.direction === 'asc' ? result : -result) || b.match.match_date.localeCompare(a.match.match_date);
    });
  }, [lookups, playerResultRows, resultSort]);
  const sortedPartnerRows = useMemo(() => {
    return [...partnerRows].sort((a, b) => {
      if (userPreferences.groupPlayersBySex) {
        const sexCompare = String(a.player1Sex || '').localeCompare(String(b.player1Sex || ''), undefined, { sensitivity: 'base' });
        if (sexCompare) return sexCompare;
      }
      const player1Compare = lookupLabel(lookups, 'players', a.player1Id).localeCompare(lookupLabel(lookups, 'players', b.player1Id));
      const player2Compare = lookupLabel(lookups, 'players', a.player2Id).localeCompare(lookupLabel(lookups, 'players', b.player2Id));
      const aPct = a.wins + a.losses > 0 ? a.wins / (a.wins + a.losses) : 0;
      const bPct = b.wins + b.losses > 0 ? b.wins / (b.wins + b.losses) : 0;
      let result = 0;
      if (partnerSort.field === 'player1') result = player1Compare;
      else if (partnerSort.field === 'player2') result = player2Compare;
      else if (partnerSort.field === 'matches') result = a.details.length - b.details.length;
      else if (partnerSort.field === 'record') result = (a.wins - a.losses) - (b.wins - b.losses);
      else if (partnerSort.field === 'win_percentage') result = aPct - bPct;
      else result = a.lastPlayed.localeCompare(b.lastPlayed);
      return (partnerSort.direction === 'asc' ? result : -result) || -((a.details.length - b.details.length) || (aPct - bPct)) || player1Compare || player2Compare;
    });
  }, [lookups, partnerRows, partnerSort, userPreferences.groupPlayersBySex]);
  const sortedCompatibilityRows = useMemo(() => {
    return [...compatibilityRows].sort((a, b) => {
      let result = 0;
      if (compatibilitySort.field === 'player1') result = lookupLabel(lookups, 'players', a.player1_id).localeCompare(lookupLabel(lookups, 'players', b.player1_id));
      else if (compatibilitySort.field === 'player2') result = lookupLabel(lookups, 'players', a.player2_id).localeCompare(lookupLabel(lookups, 'players', b.player2_id));
      else if (compatibilitySort.field === 'notes') result = String(a.notes || '').localeCompare(String(b.notes || ''), undefined, { sensitivity: 'base' });
      else result = compatibilitySortRank(a.compatibility) - compatibilitySortRank(b.compatibility);
      return (compatibilitySort.direction === 'asc' ? result : -result) || lookupLabel(lookups, 'players', a.player1_id).localeCompare(lookupLabel(lookups, 'players', b.player1_id));
    });
  }, [compatibilityRows, compatibilitySort, lookups]);
  const sortedContactRows = useMemo(() => {
    return [...contactRows].sort((a, b) => {
      if (userPreferences.groupPlayersBySex) {
        const sexCompare = String(a.sex || '').localeCompare(String(b.sex || ''), undefined, { sensitivity: 'base' });
        if (sexCompare) return sexCompare;
      }
      let result = 0;
      if (contactSort.field === 'name') {
        result = comparePlayerNames(a, b, userPreferences);
      } else {
        result = String(a[contactSort.field] || '').localeCompare(String(b[contactSort.field] || ''), undefined, { numeric: true, sensitivity: 'base' });
      }
      return (contactSort.direction === 'asc' ? result : -result) || comparePlayerNames(a, b, userPreferences);
    });
  }, [contactRows, contactSort, userPreferences]);

  useEffect(() => {
    if (!initialPlayerSchedulePlayerId) return;
    setReportType('player_schedule');
    setPlayerId(initialPlayerSchedulePlayerId);
  }, [initialPlayerSchedulePlayerId]);

  useEffect(() => {
    if (!initialReportType) return;
    setReportType(initialReportType);
  }, [initialReportType]);

  useEffect(() => {
    if (!initialParticipationTeamId) return;
    setReportType('player_participation');
    setParticipationTeamIds([initialParticipationTeamId]);
    markReportTeamSelection('player_participation');
  }, [initialParticipationTeamId]);

  useEffect(() => {
    if (validTeamIdSet.size === 0) return;
    const pruneTeams = (values: string[]) => restoreAccessibleTeamSelection(values, defaultCaptainTeamIds, [...validTeamIdSet]);
    const updateTeamList = (setter: (value: string[]) => void, values: string[]) => {
      const valid = pruneTeams(values);
      if (!sameTeamSelection(valid, values)) setter(valid);
    };
    updateTeamList(setTeamIds, teamIds);
    updateTeamList(setParticipationTeamIds, participationTeamIds);
    updateTeamList(setTeamResultsTeamIds, teamResultsTeamIds);
    updateTeamList(setResultTeamIds, resultTeamIds);
    updateTeamList(setPartnerTeamIds, partnerTeamIds);
    updateTeamList(setCompatibilityTeamIds, compatibilityTeamIds);
    updateTeamList(setTeamScheduleTeamIds, teamScheduleTeamIds);
    updateTeamList(setContactTeamIds, contactTeamIds);
    updateTeamList(setEdrTeamIds, edrTeamIds);
    if (defaultCaptainTeamIds.length === 0) {
      const invalidGlobalReportSelections: ReportType[] = [
        ...(teamIds.length > 0 && pruneTeams(teamIds).length === 0 ? ['player_schedule' as const] : []),
        ...(resultTeamIds.length > 0 && pruneTeams(resultTeamIds).length === 0 ? ['player_results' as const] : []),
        ...(partnerTeamIds.length > 0 && pruneTeams(partnerTeamIds).length === 0 ? ['partner_results' as const] : []),
        ...(teamResultsTeamIds.length > 0 && pruneTeams(teamResultsTeamIds).length === 0 ? ['team_results' as const] : []),
      ];
      if (invalidGlobalReportSelections.length > 0) setExplicitTeamReports((current) => current.filter((type) => !invalidGlobalReportSelections.includes(type)));
    }
    if (playerId && !validPlayerIdSet.has(playerId)) setPlayerId('');
    if (resultPlayerId && !validPlayerIdSet.has(resultPlayerId)) setResultPlayerId('');
    if (partnerPlayerId && !validPlayerIdSet.has(partnerPlayerId)) setPartnerPlayerId('');
  }, [compatibilityTeamIds, contactTeamIds, defaultCaptainTeamIds, edrTeamIds, participationTeamIds, partnerPlayerId, partnerTeamIds, playerId, resultPlayerId, resultTeamIds, teamIds, teamResultsTeamIds, teamScheduleTeamIds, validPlayerIdSet, validTeamIdSet]);

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    const restoredTeamIds = (type: ReportType, values: string[]) => savedViewState.explicitTeamReports === undefined || savedViewState.explicitTeamReports.includes(type)
      ? values
      : defaultCaptainTeamIds;
    setReportType(initialReportType || savedViewState.reportType);
    setPlayerId(initialPlayerSchedulePlayerId || savedViewState.playerId);
    setTeamIds(restoredTeamIds('player_schedule', savedViewState.teamIds));
    setParticipationTeamIds(initialParticipationTeamId ? [initialParticipationTeamId] : restoredTeamIds('player_participation', savedViewState.participationTeamIds));
    setTeamResultsTeamIds(restoredTeamIds('team_results', savedViewState.teamResultsTeamIds));
    setResultPlayerId(savedViewState.resultPlayerId);
    setResultTeamIds(restoredTeamIds('player_results', savedViewState.resultTeamIds));
    setPartnerTeamIds(restoredTeamIds('partner_results', savedViewState.partnerTeamIds));
    setPartnerPlayerId(savedViewState.partnerPlayerId);
    setCompatibilityTeamIds(restoredTeamIds('partner_compatibility', savedViewState.compatibilityTeamIds));
    setTeamScheduleTeamIds(restoredTeamIds('team_schedule', savedViewState.teamScheduleTeamIds || []));
    setContactTeamIds(restoredTeamIds('team_contacts', savedViewState.contactTeamIds));
    setEdrTeamIds(restoredTeamIds('edr_movement', savedViewState.edrTeamIds));
    const restoredExplicitReports = savedViewState.explicitTeamReports ?? ['player_schedule', 'player_participation', 'player_results', 'partner_results', 'partner_compatibility', 'team_schedule', 'team_contacts', 'team_results', 'edr_movement'];
    setExplicitTeamReports(initialParticipationTeamId && !restoredExplicitReports.includes('player_participation')
      ? [...restoredExplicitReports, 'player_participation']
      : restoredExplicitReports);
    setParticipationSort(savedViewState.participationSort);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, defaultCaptainTeamIds, initialParticipationTeamId, initialPlayerSchedulePlayerId, initialReportType, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || validTeamIdSet.size === 0) return;
    const scope = restoreAccessibleTeamSelection(defaultCaptainTeamIds, [], [...validTeamIdSet]);
    const applyDefault = (type: ReportType, values: string[], setter: (next: string[]) => void) => {
      if (!explicitTeamReports.includes(type) && !sameTeamSelection(values, scope)) setter(scope);
    };
    applyDefault('player_schedule', teamIds, setTeamIds);
    applyDefault('player_participation', participationTeamIds, setParticipationTeamIds);
    applyDefault('player_results', resultTeamIds, setResultTeamIds);
    applyDefault('partner_results', partnerTeamIds, setPartnerTeamIds);
    applyDefault('partner_compatibility', compatibilityTeamIds, setCompatibilityTeamIds);
    applyDefault('team_schedule', teamScheduleTeamIds, setTeamScheduleTeamIds);
    applyDefault('team_contacts', contactTeamIds, setContactTeamIds);
    applyDefault('team_results', teamResultsTeamIds, setTeamResultsTeamIds);
    applyDefault('edr_movement', edrTeamIds, setEdrTeamIds);
  }, [compatibilityTeamIds, contactTeamIds, defaultCaptainTeamIds, edrTeamIds, explicitTeamReports, participationTeamIds, partnerTeamIds, resultTeamIds, teamIds, teamResultsTeamIds, teamScheduleTeamIds, validTeamIdSet, viewStateReady]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({
      reportType,
      playerId,
      teamIds,
      participationTeamIds,
      teamResultsTeamIds,
      resultPlayerId,
      resultTeamIds,
      partnerTeamIds,
      partnerPlayerId,
      compatibilityTeamIds,
      teamScheduleTeamIds,
      contactTeamIds,
      edrTeamIds,
      explicitTeamReports,
      participationSort,
    });
  }, [compatibilityTeamIds, contactTeamIds, edrTeamIds, explicitTeamReports, participationSort, participationTeamIds, partnerPlayerId, partnerTeamIds, playerId, reportType, resultPlayerId, resultTeamIds, setSavedViewState, teamIds, teamResultsTeamIds, teamScheduleTeamIds, viewStateKey, viewStateReady]);

  usePersistentScrollRestoration({
    userId: auditUserId,
    routeKey: 'reports',
    contextKey: reportType,
    ready: !loading,
  });

  const participationSummary = useMemo(() => {
    const rosterSize = sortedParticipationRows.length;
    const totalTarget = sortedParticipationRows.reduce((sum, row) => sum + row.targetMatches, 0);
    const totalAdjustedTarget = sortedParticipationRows.reduce((sum, row) => sum + row.adjustedTarget, 0);
    const totalPlayed = sortedParticipationRows.reduce((sum, row) => sum + row.totalPlayed, 0);
    const totalLateCancels = sortedParticipationRows.reduce((sum, row) => sum + row.lateCancels, 0);
    const totalLateSubstitutes = sortedParticipationRows.reduce((sum, row) => sum + row.lateSubstitutes, 0);
    const totalFuture = sortedParticipationRows.reduce((sum, row) => sum + row.totalFuture, 0);
    const totalStatus = sortedParticipationRows.reduce((sum, row) => sum + row.statusDelta, 0);
    return {
      rosterSize,
      totalTarget,
      totalAdjustedTarget,
      totalPlayed,
      totalLateCancels,
      totalLateSubstitutes,
      totalFuture,
      averageStatus: rosterSize > 0 ? totalStatus / rosterSize : 0,
    };
  }, [sortedParticipationRows]);
  const teamResultsSummary = useMemo(() => {
    const summary = {
      wins: 0,
      losses: 0,
      ties: 0,
      unknown: 0,
      homeWins: 0,
      homeLosses: 0,
      awayWins: 0,
      awayLosses: 0,
      neutralWins: 0,
      neutralLosses: 0,
      linesWon: 0,
      linesLost: 0,
      streak: 'None',
      byLine: {} as Record<string, { wins: number; losses: number }>,
    };
    const chronological = [...teamResultRows].sort((a, b) => a.match_date.localeCompare(b.match_date));
    let streakResult = '';
    let streakCount = 0;
    chronological.forEach((match) => {
      const result = teamResultFromScores(match);
      if (result === 'Win') summary.wins += 1;
      else if (result === 'Loss') summary.losses += 1;
      else if (result === 'Tie') summary.ties += 1;
      else summary.unknown += 1;
      if (match.match_site_type === 'Home' && result === 'Win') summary.homeWins += 1;
      if (match.match_site_type === 'Home' && result === 'Loss') summary.homeLosses += 1;
      if (match.match_site_type === 'Away' && result === 'Win') summary.awayWins += 1;
      if (match.match_site_type === 'Away' && result === 'Loss') summary.awayLosses += 1;
      if (match.match_site_type === 'Neutral' && result === 'Win') summary.neutralWins += 1;
      if (match.match_site_type === 'Neutral' && result === 'Loss') summary.neutralLosses += 1;
      match.lines.forEach((line) => {
        const score = scoreLineResult(line.result);
        if (score === 0) return;
        summary.byLine[line.position.position_code] ||= { wins: 0, losses: 0 };
        if (score === 1) {
          summary.linesWon += 1;
          summary.byLine[line.position.position_code].wins += 1;
        }
        if (score === -1) {
          summary.linesLost += 1;
          summary.byLine[line.position.position_code].losses += 1;
        }
      });
      const streakCode = result === 'Win' ? 'W' : result === 'Loss' ? 'L' : result === 'Tie' ? 'T' : '';
      if (!streakCode) return;
      if (streakResult === streakCode) streakCount += 1;
      else {
        streakResult = streakCode;
        streakCount = 1;
      }
    });
    summary.streak = streakResult ? `${streakResult}${streakCount}` : 'None';
    return summary;
  }, [teamResultRows]);

  const loadReport = async () => {
    if (isUnselectedCaptainReport('player_schedule', teamIds)) { setError('Select a team to run this report.'); return; }
    if (!playerId) {
      setError('Select a player before running the report.');
      return;
    }
    if (!hasMatchGroupSelected) {
      setError('Select at least one match group to include.');
      return;
    }
    setLoading(true);
    setError(null);

    const [playedResult, lineupResult, standbyResult] = await Promise.all([
      supabase
        .from('player_participation_events')
        .select('match_event_id, league_configuration_position_id')
        .eq('player_id', playerId)
        .eq('participation_event_type', 'played')
        .is('deleted_at', null),
      supabase
        .from('lineup_entries')
        .select('match_event_id, league_configuration_position_id, slot_number, player_id')
        .eq('player_id', playerId)
        .is('deleted_at', null),
      supabase.from('standby_players').select('match_event_id, player_id, notes').eq('player_id', playerId).is('deleted_at', null),
    ]);

    if (playedResult.error || lineupResult.error || standbyResult.error) {
      setLoading(false);
      setError(playedResult.error?.message || lineupResult.error?.message || standbyResult.error?.message || 'Unable to load player schedule.');
      return;
    }

    const playedEvents = (playedResult.data || []) as unknown as ParticipationEventRow[];
    const playerLineups = (lineupResult.data || []) as unknown as LineupEntryRow[];
    const playerStandby = (standbyResult.data || []) as unknown as StandbyRow[];
    const matchIds = Array.from(new Set([...playedEvents.map((event) => event.match_event_id), ...playerLineups.map((entry) => entry.match_event_id), ...playerStandby.map((standby) => standby.match_event_id)]));

    if (matchIds.length === 0) {
      setCompletedItems([]);
      setFutureItems([]);
      setPlayersById({});
      setLoading(false);
      return;
    }

    const matchesResult = await supabase
      .from('match_events')
      .select('id, team_id, league_configuration_id, opponent_name, match_date, timezone, match_time, facility_id, opponent_average_edr, match_site_type, match_status, lineup_status')
      .in('id', matchIds)
      .is('deleted_at', null);

    if (matchesResult.error) {
      setLoading(false);
      setError(matchesResult.error.message);
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    const matches = ((matchesResult.data || []) as unknown as MatchRow[]).filter((match) => {
      if (teamIds.length > 0 && !teamIds.includes(match.team_id)) return false;
      return true;
    });
    const filteredMatchIds = matches.map((match) => match.id);

    const allPositionIds = Array.from(new Set([...playedEvents.map((event) => event.league_configuration_position_id).filter(Boolean), ...playerLineups.map((entry) => entry.league_configuration_position_id)])) as string[];
    const [allEntriesResult, positionsResult, lineResultsResult, lineStatusesResult, historicalPlayedResult] = await Promise.all([
      filteredMatchIds.length > 0
        ? supabase
            .from('lineup_entries')
            .select('match_event_id, league_configuration_position_id, slot_number, player_id')
            .in('match_event_id', filteredMatchIds)
            .is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      allPositionIds.length > 0
        ? supabase.from('league_configuration_positions').select('id, position_code, line_type').in('id', allPositionIds).is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      filteredMatchIds.length > 0
        ? supabase
            .from('line_results')
            .select('match_event_id, league_configuration_position_id, line_result, set_scores')
            .in('match_event_id', filteredMatchIds)
            .is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      filteredMatchIds.length > 0
        ? supabase
            .from('lineup_position_statuses')
            .select('match_event_id, league_configuration_position_id, line_status')
            .in('match_event_id', filteredMatchIds)
        : Promise.resolve({ data: [], error: null }),
      loadHistoricalPlayedEvents(filteredMatchIds),
    ]);

    if (allEntriesResult.error || positionsResult.error || lineResultsResult.error || lineStatusesResult.error || historicalPlayedResult.error) {
      setLoading(false);
      setError(allEntriesResult.error?.message || positionsResult.error?.message || lineResultsResult.error?.message || lineStatusesResult.error?.message || historicalPlayedResult.error?.message || 'Unable to load lineup details.');
      return;
    }

    const allEntries = effectiveHistoricalLineup((allEntriesResult.data || []) as unknown as LineupEntryRow[], historicalPlayedResult.data || []);
    const playerIds = Array.from(new Set([playerId, ...allEntries.map((entry) => entry.player_id)]));
    const playersResult = await supabase.from('players').select('id, first_name, last_name').in('id', playerIds).is('deleted_at', null);
    if (playersResult.error) {
      setLoading(false);
      setError(playersResult.error.message);
      return;
    }

    const nextPlayersById = ((playersResult.data || []) as unknown as PlayerRow[]).reduce<Record<string, PlayerRow>>((acc, player) => {
      acc[player.id] = player;
      return acc;
    }, {});
    const positionsById = ((positionsResult.data || []) as unknown as PositionRow[]).reduce<Record<string, PositionRow>>((acc, position) => {
      acc[position.id] = position;
      return acc;
    }, {});
    const entriesByMatchPosition = allEntries.reduce<Record<string, LineupEntryRow[]>>((acc, entry) => {
      acc[`${entry.match_event_id}:${entry.league_configuration_position_id}`] ||= [];
      acc[`${entry.match_event_id}:${entry.league_configuration_position_id}`].push(entry);
      return acc;
    }, {});
    const lineResultByMatchPosition = ((lineResultsResult.data || []) as unknown as LineResultRow[]).reduce<Record<string, LineResultRow>>((acc, result) => {
      acc[`${result.match_event_id}:${result.league_configuration_position_id}`] = result;
      return acc;
    }, {});
    const lineStatusByMatchPosition = ((lineStatusesResult.data || []) as unknown as LineStatusRow[]).reduce<Record<string, LineStatusRow['line_status']>>((acc, result) => {
      acc[`${result.match_event_id}:${result.league_configuration_position_id}`] = result.line_status;
      return acc;
    }, {});
    const playedByMatch = authoritativePlayedEvents(historicalPlayedResult.data || []).filter(event => event.player_id === playerId).reduce<Record<string, ParticipationEventRow[]>>((acc, event) => {
      acc[event.match_event_id] ||= [];
      acc[event.match_event_id].push(event);
      return acc;
    }, {});
    const playerLineupByMatch = allEntries.filter(entry => entry.player_id === playerId).reduce<Record<string, LineupEntryRow[]>>((acc, entry) => {
      acc[entry.match_event_id] ||= [];
      acc[entry.match_event_id].push(entry);
      return acc;
    }, {});
    const standbyByMatch = playerStandby.reduce<Record<string, StandbyRow[]>>((acc, standby) => {
      acc[standby.match_event_id] ||= [];
      acc[standby.match_event_id].push(standby);
      return acc;
    }, {});

    const completed: ScheduleItem[] = [];
    const future: ScheduleItem[] = [];
    matches.forEach((match) => {
      if (includeCompleted && match.match_status === 'Completed') {
        const played = playedByMatch[match.id] || [];
        const completedPositionIds = played.length > 0 ? played.map((event) => event.league_configuration_position_id).filter(Boolean) : (playerLineupByMatch[match.id] || []).map((entry) => entry.league_configuration_position_id);
        Array.from(new Set(completedPositionIds)).forEach((positionId) => {
          if (!positionId) return;
          completed.push({
            match,
            position: positionsById[positionId],
            lineupEntries: entriesByMatchPosition[`${match.id}:${positionId}`] || [],
            isStandby: false,
            lineStatus: lineStatusByMatchPosition[`${match.id}:${positionId}`],
            lineResult: lineResultByMatchPosition[`${match.id}:${positionId}`],
          });
        });
      }

      if (includeFuture && match.match_date >= today && match.match_status !== 'Cancelled' && match.match_status !== 'Completed') {
        (playerLineupByMatch[match.id] || []).forEach((entry) => {
          future.push({
            match,
            position: positionsById[entry.league_configuration_position_id],
            lineupEntries: entriesByMatchPosition[`${match.id}:${entry.league_configuration_position_id}`] || [],
            isStandby: false,
            lineStatus: lineStatusByMatchPosition[`${match.id}:${entry.league_configuration_position_id}`],
          });
        });
        (standbyByMatch[match.id] || []).forEach(() => {
          future.push({ match, lineupEntries: [], isStandby: true });
        });
      }
    });

    completed.sort(compareScheduleItemsAscending);
    future.sort(compareScheduleItemsAscending);

    setCompletedItems(completed);
    setFutureItems(future);
    setPlayersById(nextPlayersById);
    setLoading(false);
  };

  useEffect(() => {
    if (!initialPlayerSchedulePlayerId || playerId !== initialPlayerSchedulePlayerId || autoLoadedSchedulePlayerId === initialPlayerSchedulePlayerId) return;
    setAutoLoadedSchedulePlayerId(initialPlayerSchedulePlayerId);
    void loadReport();
  }, [autoLoadedSchedulePlayerId, initialPlayerSchedulePlayerId, playerId]);

  const reportText = copyTextForReport(selectedPlayerName || 'Player', visibleCompletedItems, visibleFutureItems, playersById, lookups, userPreferences, timeFormat);

  const copySchedule = async () => {
    if (!hasMatchGroupSelected) {
      setError('Select at least one match group to include.');
      return;
    }
    try {
      await navigator.clipboard.writeText(reportText);
      setSnackMessage('Player schedule copied.');
    } catch {
      setManualCopyText(reportText);
    }
  };

  const loadParticipationReport = async () => {
    if (participationTeamIds.length === 0) {
      setError('Select at least one team.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const results = await Promise.all(
        participationTeamIds.map(async (selectedTeamId) => {
          const { rows } = await loadParticipationReportRows({
            teamId: selectedTeamId,
            includeInactivePlayers,
            includeSubstitutePlayers,
            userPreferences,
            playerLabel: (playerId) => lookupLabel(lookups, 'players', playerId),
          });
          const teamName = lookupLabel(lookups, 'teams', selectedTeamId);
          return rows.map((row) => ({
            ...row,
            playerName: participationTeamIds.length > 1 ? `${row.playerName} (${teamName})` : row.playerName,
          }));
        }),
      );
      setParticipationRows(results.flat());
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to load participation counts.');
    } finally {
      setLoading(false);
    }
  };

  const loadResultsBaseData = async (completedOnly = true) => {
    let matchesQuery = supabase
      .from('match_events')
      .select('id, team_id, league_configuration_id, opponent_name, match_date, timezone, match_time, warmup_time, facility_id, opponent_average_edr, match_site_type, match_status, lineup_status, match_results(result_status, our_team_score, opponent_team_score, team_result_value)')
      .is('deleted_at', null);
    if (completedOnly) matchesQuery = matchesQuery.eq('match_status', 'Completed');
    const [matchesResult, teamsResult, positionsResult, lineResultsResult, entriesResult, lineStatusesResult, playedResult, playersResult, participationResult] = await Promise.all([
      matchesQuery,
      supabase.from('teams').select('id, organization_id, team_name').is('deleted_at', null),
      supabase.from('league_configuration_positions').select('id, league_configuration_id, position_code, line_type, display_order').is('deleted_at', null),
      supabase.from('line_results').select('match_event_id, league_configuration_position_id, line_result, result_value, set_scores').is('deleted_at', null),
      supabase.from('lineup_entries').select('match_event_id, league_configuration_position_id, slot_number, player_id').is('deleted_at', null),
      supabase.from('lineup_position_statuses').select('match_event_id, league_configuration_position_id, line_status'),
      supabase.from('player_participation_events').select('match_event_id, league_configuration_position_id, player_id, participation_event_reason').eq('participation_event_type', 'played').is('deleted_at', null),
      supabase.from('players').select('id, first_name, last_name, sex, estimated_dynamic_rating').is('deleted_at', null),
      supabase.from('player_participation_events').select('match_event_id, player_id, participation_event_type').in('participation_event_type', ['cancelled_late', 'late_substitute', 'standby_promotion']).is('deleted_at', null),
    ]);
    const loadError = matchesResult.error || teamsResult.error || positionsResult.error || lineResultsResult.error || entriesResult.error || lineStatusesResult.error || playedResult.error || playersResult.error || participationResult.error;
    if (loadError) throw new Error(loadError.message);
    const teamsById = new Map(((teamsResult.data || []) as unknown as { id: string; organization_id: string | null }[]).map((team) => [team.id, team.organization_id]));
    const matches = ((matchesResult.data || []) as unknown as (MatchRow & { match_results?: TeamResultRow['match_results'] })[]).map((match) => ({
      ...match,
      organization_id: teamsById.get(match.team_id) || null,
      result_status: match.match_results?.[0]?.result_status || 'Not Started',
      our_team_score: match.match_results?.[0]?.our_team_score || 0,
      opponent_team_score: match.match_results?.[0]?.opponent_team_score || 0,
      team_result_value: match.match_results?.[0]?.team_result_value || 'Unknown',
    })) as ResultsReportMatch[];
    return {
      matches,
      positions: (positionsResult.data || []) as unknown as PositionRow[],
      lineResults: (lineResultsResult.data || []) as unknown as LineResultRow[],
      entries: effectiveHistoricalLineup((entriesResult.data || []) as unknown as LineupEntryRow[], playedResult.data || []),
      lineStatuses: (lineStatusesResult.data || []) as unknown as LineStatusRow[],
      played: authoritativePlayedEvents(playedResult.data || []) as unknown as (ParticipationEventRow & { player_id: string })[],
      playersById: ((playersResult.data || []) as unknown as PlayerRow[]).reduce<Record<string, PlayerRow>>((acc, player) => ({ ...acc, [player.id]: player }), {}),
      participation: (participationResult.data || []) as unknown as { match_event_id: string; player_id: string; participation_event_type: string }[],
    };
  };

  const matchPassesResultFilters = (match: ResultsReportMatch, filters: { teamIds?: string[]; dateFrom?: string; dateTo?: string; siteType?: string }) => {
    if (filters.teamIds && filters.teamIds.length > 0 && !filters.teamIds.includes(match.team_id)) return false;
    if (filters.dateFrom && match.match_date < filters.dateFrom) return false;
    if (filters.dateTo && match.match_date > filters.dateTo) return false;
    if (filters.siteType && match.match_site_type !== filters.siteType) return false;
    return true;
  };

  const loadPlayerResultsReport = async () => {
    if (isUnselectedCaptainReport('player_results', resultTeamIds)) { setError('Select a team to run this report.'); return; }
    if (!resultPlayerId) {
      setError('Player is required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const base = await loadResultsBaseData();
      const matchById = new Map(base.matches.map((match) => [match.id, match]));
      const positionById = new Map(base.positions.map((position) => [position.id, position]));
      const entriesByMatchPosition = base.entries.reduce<Record<string, LineupEntryRow[]>>((acc, entry) => {
        acc[`${entry.match_event_id}:${entry.league_configuration_position_id}`] ||= [];
        acc[`${entry.match_event_id}:${entry.league_configuration_position_id}`].push(entry);
        return acc;
      }, {});
      const lineResultByKey = new Map(base.lineResults.map((line) => [`${line.match_event_id}:${line.league_configuration_position_id}`, line]));
      const participantKeys = resultParticipantsByLine(base.entries, base.played);
      const rows: PlayerResultDetail[] = [];
      participantKeys.forEach((playerIds, key) => {
        if (!playerIds.includes(resultPlayerId)) return;
        const [matchId, positionId] = key.split(':');
        const match = matchById.get(matchId);
        const position = positionById.get(positionId);
        const line = lineResultByKey.get(key);
        if (!match || !position || !line?.result_value) return;
        if (!matchPassesResultFilters(match, { teamIds: resultTeamIds, dateFrom: resultDateFrom, dateTo: resultDateTo, siteType: resultSiteType })) return;
        if (resultLineType && position.line_type !== resultLineType) return;
        if (!isPlayedLineResult(line.result_value) || scoreLineResult(line.result_value) === 0) return;
        const partners = (entriesByMatchPosition[key] || [])
          .filter((entry) => entry.player_id !== resultPlayerId)
          .map((entry) => formatPlayerName(base.playersById[entry.player_id], userPreferences))
          .filter(Boolean);
        rows.push({ id: key, match, position, partners, lineResult: line.result_value });
      });
      const summary: PlayerResultsSummary = { wins: 0, losses: 0, singlesWins: 0, singlesLosses: 0, doublesWins: 0, doublesLosses: 0, mixedWins: 0, mixedLosses: 0, homeWins: 0, homeLosses: 0, awayWins: 0, awayLosses: 0, neutralWins: 0, neutralLosses: 0, lateCancels: 0, lateSubstitutes: 0 };
      rows.forEach((row) => {
        const score = scoreLineResult(row.lineResult);
        if (score === 1) summary.wins += 1;
        if (score === -1) summary.losses += 1;
        if (row.position.line_type === 'singles') score === 1 ? summary.singlesWins += 1 : summary.singlesLosses += 1;
        if (row.position.line_type === 'doubles') score === 1 ? summary.doublesWins += 1 : summary.doublesLosses += 1;
        if (row.position.line_type === 'mixed_doubles') score === 1 ? summary.mixedWins += 1 : summary.mixedLosses += 1;
        if (row.match.match_site_type === 'Home') score === 1 ? summary.homeWins += 1 : summary.homeLosses += 1;
        if (row.match.match_site_type === 'Away') score === 1 ? summary.awayWins += 1 : summary.awayLosses += 1;
        if (row.match.match_site_type === 'Neutral') score === 1 ? summary.neutralWins += 1 : summary.neutralLosses += 1;
      });
      summary.lateCancels = base.participation.filter((event) => event.player_id === resultPlayerId && event.participation_event_type === 'cancelled_late').length;
      summary.lateSubstitutes = base.participation.filter((event) => event.player_id === resultPlayerId && ['late_substitute', 'standby_promotion'].includes(event.participation_event_type)).length;
      setPlayerResultRows(rows);
      setPlayerResultSummary(summary);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load player results.');
    } finally {
      setLoading(false);
    }
  };

  const loadPartnerResultsReport = async () => {
    if (isUnselectedCaptainReport('partner_results', partnerTeamIds)) { setError('Select a team to run this report.'); return; }
    setLoading(true);
    setError(null);
    try {
      const base = await loadResultsBaseData();
      const matchById = new Map(base.matches.map((match) => [match.id, match]));
      const positionById = new Map(base.positions.map((position) => [position.id, position]));
      const lineResultByKey = new Map(base.lineResults.map((line) => [`${line.match_event_id}:${line.league_configuration_position_id}`, line]));
      const participantsByMatchPosition = resultParticipantsByLine(base.entries, base.played);
      const pairs = new Map<string, PartnerSummaryRow>();
      participantsByMatchPosition.forEach((playerIds, key) => {
        const [matchId, positionId] = key.split(':');
        const match = matchById.get(matchId);
        const position = positionById.get(positionId);
        const line = lineResultByKey.get(key);
        if (!match || !position || !line?.result_value || position.line_type === 'singles') return;
        if (!matchPassesResultFilters(match, { teamIds: partnerTeamIds, dateFrom: partnerDateFrom, dateTo: partnerDateTo, siteType: partnerSiteType })) return;
        if (!isPlayedLineResult(line.result_value) || scoreLineResult(line.result_value) === 0) return;
        const uniquePlayers = [...new Set(playerIds)].sort();
        if (uniquePlayers.length < 2) return;
        for (let i = 0; i < uniquePlayers.length - 1; i += 1) {
          for (let j = i + 1; j < uniquePlayers.length; j += 1) {
            if (partnerPlayerId && ![uniquePlayers[i], uniquePlayers[j]].includes(partnerPlayerId)) continue;
            const pairKey = `${uniquePlayers[i]}:${uniquePlayers[j]}`;
            const existing = pairs.get(pairKey) || {
              key: pairKey,
              player1Id: uniquePlayers[i],
              player2Id: uniquePlayers[j],
              player1Sex: base.playersById[uniquePlayers[i]]?.sex || null,
              player2Sex: base.playersById[uniquePlayers[j]]?.sex || null,
              teams: [],
              details: [],
              wins: 0,
              losses: 0,
              homeWins: 0,
              homeLosses: 0,
              awayWins: 0,
              awayLosses: 0,
              lastPlayed: '',
            };
            existing.details.push({ id: `${key}:${i}:${j}`, player1Id: uniquePlayers[i], player2Id: uniquePlayers[j], match, position, lineResult: line.result_value });
            if (!existing.teams.includes(match.team_id)) existing.teams.push(match.team_id);
            const score = scoreLineResult(line.result_value);
            if (score === 1) existing.wins += 1;
            if (score === -1) existing.losses += 1;
            if (match.match_site_type === 'Home') score === 1 ? existing.homeWins += 1 : existing.homeLosses += 1;
            if (match.match_site_type === 'Away') score === 1 ? existing.awayWins += 1 : existing.awayLosses += 1;
            existing.lastPlayed = existing.lastPlayed > match.match_date ? existing.lastPlayed : match.match_date;
            pairs.set(pairKey, existing);
          }
        }
      });
      const minMatches = Math.max(1, Number(partnerMinimumMatches || 1));
      setPartnerRows([...pairs.values()].filter((row) => row.details.length >= minMatches));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load partner results.');
    } finally {
      setLoading(false);
    }
  };

  const loadPartnerCompatibilityReport = async () => {
    if (!canViewPartnerPreferences) {
      setError('Partner Preferences access is required for the partner compatibility report.');
      setCompatibilityRows([]);
      return;
    }
    if (compatibilityTeamIds.length === 0) {
      setError('Select at least one team for the compatibility report.');
      return;
    }
    const allowedTeamIds = new Set(readablePartnerPreferenceTeamIds);
    const requestedTeamIds = compatibilityTeamIds.filter((teamId) => allowedTeamIds.has(teamId));
    const workspaceIds = [...new Set(requestedTeamIds.map((teamId) => lookups.teams.find((team) => team.value === teamId)?.workspace_id).filter(Boolean))] as string[];
    if (workspaceIds.length === 0) {
      setError('Selected teams do not have a workspace.');
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: compatibilityError } = await supabase
      .from('partner_preferences')
      .select('id, workspace_id, team_id, player1_id, player2_id, compatibility, pairing_rule, is_active, notes')
      .in('workspace_id', workspaceIds)
      .eq('is_active', true)
      .is('deleted_at', null);

    setLoading(false);
    if (compatibilityError) {
      setError(compatibilityError.message);
      return;
    }
    setCompatibilityRows((data || []) as unknown as PartnerCompatibilityRow[]);
  };

  const copyPlayerResultsReport = async () => {
    if (!resultPlayerId) {
      setError('Player is required.');
      return;
    }
    const recent = sortedPlayerResultRows.slice(0, 10).map((row) => {
      const partner = row.partners.length > 0 ? ` with ${row.partners.join(' / ')}` : '';
      return `${formatDate(localEventDate(row.match))} - ${lookupLabel(lookups, 'teams', row.match.team_id)} vs ${row.match.opponent_name || 'TBD'} - ${row.position.position_code}${partner} - ${resultLabel(row.lineResult)}`;
    });
    const text = [
      `${resultPlayerName || 'Player'} Results`,
      '',
      `Overall: ${recordText(playerResultSummary.wins, playerResultSummary.losses)}`,
      `Singles: ${recordText(playerResultSummary.singlesWins, playerResultSummary.singlesLosses)}`,
      `Doubles: ${recordText(playerResultSummary.doublesWins, playerResultSummary.doublesLosses)}`,
      `Mixed Doubles: ${recordText(playerResultSummary.mixedWins, playerResultSummary.mixedLosses)}`,
      `Home: ${recordText(playerResultSummary.homeWins, playerResultSummary.homeLosses)}`,
      `Away: ${recordText(playerResultSummary.awayWins, playerResultSummary.awayLosses)}`,
      '',
      'Recent Matches:',
      ...(recent.length > 0 ? recent : ['No completed results found.']),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setSnackMessage('Player results copied.');
    } catch {
      setManualCopyText(text);
    }
  };

  const copyPartnerResultsReport = async () => {
    const body = sortedPartnerRows.flatMap((row) => [
      `${lookupLabel(lookups, 'players', row.player1Id)} / ${lookupLabel(lookups, 'players', row.player2Id)}`,
      `Record: ${recordText(row.wins, row.losses)}`,
      `Win %: ${winPercentage(row.wins, row.losses)}`,
      '',
    ]);
    const text = ['Partner Results', '', ...(body.length > 0 ? body : ['No partner results found.'])].join('\n').trimEnd();
    try {
      await navigator.clipboard.writeText(text);
      setSnackMessage('Partner results copied.');
    } catch {
      setManualCopyText(text);
    }
  };

  const copyPartnerCompatibilityReport = async () => {
    const body = sortedCompatibilityRows.flatMap((row) => [
      `${lookupLabel(lookups, 'players', row.player1_id)} / ${lookupLabel(lookups, 'players', row.player2_id)}`,
      `Compatibility: ${row.compatibility || 'Unknown'}`,
      `Scheduling Preference: ${pairingRuleLabels[normalizePairingRule(row.pairing_rule)]}`,
      row.notes ? `Notes: ${row.notes}` : 'Notes: -',
      '',
    ]);
    const text = ['Partner Compatibility', '', ...(body.length > 0 ? body : ['No partner preferences found.'])].join('\n').trimEnd();
    try {
      await navigator.clipboard.writeText(text);
      setSnackMessage('Partner compatibility copied.');
    } catch {
      setManualCopyText(text);
    }
  };

  const visibleContactTeamIds = () => {
    const allowed = new Set(filteredContactTeams.map((team) => team.value));
    return contactTeamIds.filter((teamId) => allowed.has(teamId));
  };

  const loadTeamContactReport = async () => {
    const teamIds = visibleContactTeamIds();
    if (teamIds.length === 0) {
      setError('Select at least one team for the contact report.');
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: contactError } = await supabase
      .from('team_rosters')
      .select('team_id, player_id, players!inner(id, first_name, last_name, sex, email, mobile_phone, city, state, is_active)')
      .in('team_id', teamIds)
      .eq('is_active', true)
      .is('deleted_at', null)
      .eq('players.is_active', true)
      .is('players.deleted_at', null);
    setLoading(false);
    if (contactError) {
      setError(contactError.message);
      return;
    }
    const rows = ((data || []) as unknown as Array<{ team_id: string; player_id: string; players: ContactReportRow }>).map((row) => ({
      key: `${row.team_id}:${row.player_id}`,
      playerId: row.player_id,
      teamId: row.team_id,
      first_name: row.players?.first_name || null,
      last_name: row.players?.last_name || null,
      sex: row.players?.sex || null,
      mobile_phone: row.players?.mobile_phone || null,
      email: row.players?.email || null,
      city: row.players?.city || null,
      state: row.players?.state || null,
    }));
    if (!contactDeduplicate) {
      setContactRows(rows);
      return;
    }
    const deduped = new Map<string, ContactReportRow>();
    rows.forEach((row) => {
      if (!deduped.has(row.playerId)) deduped.set(row.playerId, row);
    });
    setContactRows([...deduped.values()]);
  };

  const loadTeamScheduleReport = async () => {
    if (teamScheduleTeamIds.length === 0) {
      setError('Select at least one team for the schedule report.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [base, rosterResult] = await Promise.all([
        loadResultsBaseData(false),
        supabase
          .from('team_rosters')
          .select('team_id, player_id')
          .in('team_id', teamScheduleTeamIds)
          .eq('is_active', true)
          .is('deleted_at', null),
      ]);
      if (rosterResult.error) throw new Error(rosterResult.error.message);
      const entriesByMatchPosition = base.entries.reduce<Record<string, LineupEntryRow[]>>((acc, entry) => {
        const key = `${entry.match_event_id}:${entry.league_configuration_position_id}`;
        acc[key] ||= [];
        acc[key].push(entry);
        return acc;
      }, {});
      const resultByMatchPosition = new Map(base.lineResults.map((line) => [`${line.match_event_id}:${line.league_configuration_position_id}`, line]));
      const statusByMatchPosition = new Map(base.lineStatuses.map((line) => [`${line.match_event_id}:${line.league_configuration_position_id}`, line.line_status]));
      const playedByMatchPosition = resultParticipantsByLine(base.entries, base.played);

      const rows = base.matches
        .filter((match) => {
          if (!teamScheduleTeamIds.includes(match.team_id)) return false;
          if (teamScheduleDateFrom && match.match_date < teamScheduleDateFrom) return false;
          if (teamScheduleDateTo && match.match_date > teamScheduleDateTo) return false;
          return true;
        })
        .map<TeamScheduleRow>((match) => {
          const positions = base.positions
            .filter((position) => position.league_configuration_id === match.league_configuration_id)
            .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999) || a.position_code.localeCompare(b.position_code, undefined, { numeric: true }));
          return {
            ...match,
            warmup_time: match.warmup_time || null,
            lines: positions.map((position) => {
              const key = `${match.id}:${position.id}`;
              const playerIds = match.match_status === 'Completed' && playedByMatchPosition.has(key)
                ? playedByMatchPosition.get(key) || []
                : (entriesByMatchPosition[key] || []).sort((a, b) => a.slot_number - b.slot_number).map((entry) => entry.player_id);
              return {
                position,
                players: playerIds.map((playerId) => formatPlayerName(base.playersById[playerId], userPreferences) || lookupLabel(lookups, 'players', playerId) || 'Unknown Player'),
                lineStatus: statusByMatchPosition.get(key),
                result: resultByMatchPosition.get(key) || null,
              };
            }),
          };
        })
        .sort((a, b) => a.match_date.localeCompare(b.match_date) || (a.match_time || '').localeCompare(b.match_time || '') || lookupLabel(lookups, 'teams', a.team_id).localeCompare(lookupLabel(lookups, 'teams', b.team_id)));

      const selectedMatchIds = new Set(rows.map((match) => match.id));
      const selectedMatchTeamById = new Map(rows.map((match) => [match.id, match.team_id]));
      const scheduledKeys = new Set(base.entries.filter((entry) => selectedMatchIds.has(entry.match_event_id)).map((entry) => `${entry.player_id}:${entry.match_event_id}:${entry.league_configuration_position_id}`));
      const playedKeys = new Set(base.played.filter((event) => selectedMatchIds.has(event.match_event_id)).map((event) => `${event.player_id}:${event.match_event_id}:${event.league_configuration_position_id || ''}`));
      const summaryByPlayer = new Map<string, TeamSchedulePlayerSummaryRow>();
      const ensureSummary = (playerId: string) => {
        const existing = summaryByPlayer.get(playerId);
        if (existing) return existing;
        const row = {
          playerId,
          playerName: formatPlayerName(base.playersById[playerId], userPreferences) || lookupLabel(lookups, 'players', playerId) || 'Unknown Player',
          teamNames: [] as string[],
          scheduled: 0,
          played: 0,
        };
        summaryByPlayer.set(playerId, row);
        return row;
      };
      ((rosterResult.data || []) as unknown as { team_id: string; player_id: string }[]).forEach((roster) => {
        const row = ensureSummary(roster.player_id);
        const teamName = lookupLabel(lookups, 'teams', roster.team_id);
        if (teamName && !row.teamNames.includes(teamName)) row.teamNames.push(teamName);
      });
      scheduledKeys.forEach((key) => {
        const [playerId, matchId] = key.split(':');
        const row = ensureSummary(playerId);
        const teamName = lookupLabel(lookups, 'teams', selectedMatchTeamById.get(matchId) || '');
        if (teamName && !row.teamNames.includes(teamName)) row.teamNames.push(teamName);
        row.scheduled += 1;
      });
      playedKeys.forEach((key) => {
        const [playerId, matchId] = key.split(':');
        const row = ensureSummary(playerId);
        const teamName = lookupLabel(lookups, 'teams', selectedMatchTeamById.get(matchId) || '');
        if (teamName && !row.teamNames.includes(teamName)) row.teamNames.push(teamName);
        row.played += 1;
      });

      setTeamScheduleRows(rows);
      setTeamSchedulePlayerSummaryRows([...summaryByPlayer.values()].sort((a, b) => comparePlayerNames({ first_name: a.playerName, last_name: '' }, { first_name: b.playerName, last_name: '' }, userPreferences)));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load team schedule.');
    } finally {
      setLoading(false);
    }
  };

  const copyTeamScheduleReport = async () => {
    const lines = [
      `${teamScheduleTeamName} Schedule`,
      '',
      ...teamScheduleRows.flatMap((match) => [
        `${formatDate(localEventDate(match))}${formatTime(match.match_time, timeFormat, match) ? ` ${formatTime(match.match_time, timeFormat, match)}` : ''} vs ${match.opponent_name || 'TBD'}`,
        `Team: ${lookupLabel(lookups, 'teams', match.team_id)}`,
        `Location: ${lookupLabel(lookups, 'facilities', match.facility_id) || 'Location not set'}`,
        `Warmup: ${formatTime(match.warmup_time, timeFormat, match) || 'Not set'}`,
        `Status: ${match.match_status} / ${match.lineup_status || 'Not Started'}`,
        ...match.lines.map((line) => `${line.position.position_code}: ${teamScheduleLineDisplay(line, match.match_status === 'Completed')}`),
        '',
      ]),
      'Player Summary:',
      ...teamSchedulePlayerSummaryRows.map((row) => `${row.playerName}: ${row.scheduled} scheduled${row.played > 0 ? `, ${row.played} played` : ''}`),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n').trim());
      setSnackMessage('Team schedule copied.');
    } catch {
      setManualCopyText(lines.join('\n').trim());
    }
  };

  const contactExportRows = () => {
    const headers = ['Player Name', ...(contactIncludePhone ? ['Phone Number'] : []), ...(contactIncludeEmail ? ['Email Address'] : []), ...(contactIncludeCityState ? ['City', 'State'] : [])];
    const rows = sortedContactRows.map((row) => [
      formatPlayerName(row, userPreferences),
      ...(contactIncludePhone ? [row.mobile_phone || ''] : []),
      ...(contactIncludeEmail ? [row.email || ''] : []),
      ...(contactIncludeCityState ? [row.city || '', row.state || ''] : []),
    ]);
    return { headers, rows };
  };

  const contactReportTitle = () => {
    if (contactTeamIds.length === 1) return lookupLabel(lookups, 'teams', contactTeamIds[0]);
    return contactTeamIds.length > 1 ? 'Selected Teams' : 'Team Contact Report';
  };

  const contactCsvText = () => {
    const { headers, rows } = contactExportRows();
    return [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
  };

  const copyContactList = async () => {
    const blocks = sortedContactRows.map((row) => {
      const lines = [formatPlayerName(row, userPreferences)];
      if (contactIncludePhone && row.mobile_phone) lines.push(row.mobile_phone);
      if (contactIncludeEmail && row.email) lines.push(`[${row.email}](mailto:${row.email})`);
      if (contactIncludeCityState && (row.city || row.state)) lines.push([row.city, row.state].filter(Boolean).join(', '));
      return lines.join('\n');
    });
    const text = [contactReportTitle(), '', ...blocks].join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setSnackMessage('Contact list copied.');
    } catch {
      setManualCopyText(text);
    }
  };

  const exportContactCsv = () => {
    const csv = contactCsvText();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${compactFilePart(contactReportTitle()) || 'TeamContact'}_Roster_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const openContactGoogleSheets = async () => {
    const { headers, rows } = contactExportRows();
    const tsv = [headers, ...rows].map((row) => row.join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
      window.open('https://sheets.new', '_blank', 'noopener,noreferrer');
      setSnackMessage('Spreadsheet data copied. Paste it into the new Google Sheet.');
    } catch {
      setManualCopyText(tsv);
    }
  };

  const copyParticipationReport = async () => {
    if (participationTeamIds.length === 0) {
      setError('Select at least one team.');
      return;
    }
    const text = copyParticipationText(participationTeamName || 'Selected Teams', sortedParticipationRows);
    try {
      await navigator.clipboard.writeText(text);
      setSnackMessage('Participation report copied.');
    } catch {
      setManualCopyText(text);
    }
  };

  const loadTeamResultsReport = async () => {
    if (isUnselectedCaptainReport('team_results', teamResultsTeamIds)) { setError('Select a team to run this report.'); return; }
    setLoading(true);
    setError(null);
    try {
      const base = await loadResultsBaseData(false);
      const positionById = new Map(base.positions.map((position) => [position.id, position]));
      const lineResultsByMatch = base.lineResults.reduce<Record<string, LineResultRow[]>>((acc, line) => {
        acc[line.match_event_id] ||= [];
        acc[line.match_event_id].push(line);
        return acc;
      }, {});
      const entriesByMatch = base.entries.reduce<Record<string, LineupEntryRow[]>>((acc, entry) => {
        acc[entry.match_event_id] ||= [];
        acc[entry.match_event_id].push(entry);
        return acc;
      }, {});
      const rows = base.matches
        .filter((match) => {
          if (teamResultsTeamIds.length > 0 && !teamResultsTeamIds.includes(match.team_id)) return false;
          if (teamResultsMatchStatus && match.match_status !== teamResultsMatchStatus) return false;
          if (teamResultsResultStatus && match.result_status !== teamResultsResultStatus) return false;
          if (teamResultsDateFrom && match.match_date < teamResultsDateFrom) return false;
          if (teamResultsDateTo && match.match_date > teamResultsDateTo) return false;
          if (teamResultsSiteType && match.match_site_type !== teamResultsSiteType) return false;
          if (teamResultsResultValue && teamResultFromScores(match) !== teamResultsResultValue) return false;
          return true;
        })
        .map<TeamResultDetailRow>((match) => ({
          ...match,
          lineup_average_edr: calculateLineupAverageEdr(
            (entriesByMatch[match.id] || [])
              .map((entry) => base.playersById[entry.player_id])
              .filter((player): player is PlayerRow => Boolean(player)),
          ),
          lines: (lineResultsByMatch[match.id] || [])
            .map((line) => {
              const position = positionById.get(line.league_configuration_position_id);
              return position && line.result_value ? { position, result: line.result_value } : null;
            })
            .filter((line): line is { position: PositionRow; result: LineResultValue } => Boolean(line))
            .sort((a, b) => a.position.position_code.localeCompare(b.position.position_code, undefined, { numeric: true })),
        }))
        .sort((a, b) => b.match_date.localeCompare(a.match_date));
      setTeamResultRows(rows);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load team results.');
    } finally {
      setLoading(false);
    }
  };

  const copyTeamResultsReport = async () => {
    const lines = [
      `${teamResultsTeamName || 'Selected Teams'} Results`,
      '',
      `Overall: ${recordText(teamResultsSummary.wins, teamResultsSummary.losses)}`,
      `Home: ${recordText(teamResultsSummary.homeWins, teamResultsSummary.homeLosses)}`,
      `Away: ${recordText(teamResultsSummary.awayWins, teamResultsSummary.awayLosses)}`,
      `Current Streak: ${teamResultsSummary.streak}`,
      `Line Record: ${recordText(teamResultsSummary.linesWon, teamResultsSummary.linesLost)}`,
      '',
      'By Line:',
      ...Object.entries(teamResultsSummary.byLine).map(([line, record]) => `${line}: ${recordText(record.wins, record.losses)}`),
      '',
      ...teamResultRows.map((match) => {
        const date = formatDate(localEventDate(match));
        const time = formatTime(match.match_time, timeFormat, match);
        return `${date}${time ? ` ${time}` : ''} vs ${match.opponent_name || 'TBD'}: ${teamResultFromScores(match)}, Score ${match.our_team_score}-${match.opponent_team_score}, Opponent EDR ${formatEdr(match.opponent_average_edr)}, Lineup EDR ${formatEdr(match.lineup_average_edr)}`;
      }),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setSnackMessage('Team results copied.');
    } catch {
      setManualCopyText(lines.join('\n'));
    }
  };

  const selectedEdrTeams = async (): Promise<EdrTeamRow[]> => {
    if (edrTeamIds.length === 0) return [];
    const { data, error: teamsError } = await supabase
      .from('teams')
      .select('id, team_name, workspace_id, season_label')
      .in('id', edrTeamIds)
      .is('deleted_at', null);
    if (teamsError) throw new Error(teamsError.message);
    return (data || []) as unknown as EdrTeamRow[];
  };

  const loadEdrMovementReport = async () => {
    if (edrTeamIds.length === 0) {
      setError('Select at least one team for the EDR movement report.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const teams = await selectedEdrTeams();
      const teamById = new Map(teams.map((team) => [team.id, team]));
      const [rosterResult, snapshotResult, dateRangeResult] = await Promise.all([
        supabase
          .from('team_rosters')
          .select('team_id, player_id, players!inner(id, first_name, last_name, sex, estimated_dynamic_rating, tennis_record_profile_url)')
          .in('team_id', edrTeamIds)
          .eq('is_active', true)
          .is('deleted_at', null)
          .eq('players.is_active', true)
          .is('players.deleted_at', null),
        supabase
          .from('player_edr_season_snapshots')
          .select('id, workspace_id, team_id, player_id, snapshot_type, snapshot_label, baseline_edr, captured_at, source, tennis_record_profile_url')
          .in('team_id', edrTeamIds)
          .eq('snapshot_type', 'season_start'),
        supabase
          .from('team_playing_date_range')
          .select('team_id, first_match_date, last_match_date')
          .in('team_id', edrTeamIds),
      ]);
      if (rosterResult.error || snapshotResult.error || dateRangeResult.error) throw new Error(rosterResult.error?.message || snapshotResult.error?.message || dateRangeResult.error?.message || 'Unable to load EDR report data.');
      const dateRangeByTeam = new Map(((dateRangeResult.data || []) as TeamPlayingDateRange[]).map((row) => [row.team_id, row]));
      const rosterRows = (rosterResult.data || []) as unknown as EdrRosterRow[];
      const playerIds = Array.from(new Set(rosterRows.map((row) => row.player_id)));
      const syncResult = playerIds.length > 0
        ? await supabase
            .from('tennis_record_sync_runs')
            .select('player_id, started_at, completed_at, observed_value, prior_value, changed, status')
            .in('player_id', playerIds)
            .eq('sync_type', 'player_profile')
            .eq('status', 'success')
            .not('observed_value', 'is', null)
            .order('started_at', { ascending: true })
        : { data: [], error: null };
      if (syncResult.error) throw new Error(syncResult.error.message);

      const snapshots = (snapshotResult.data || []) as unknown as EdrSnapshotRow[];
      const snapshotByTeamPlayer = new Map(snapshots.map((snapshot) => [`${snapshot.team_id}:${snapshot.player_id}`, snapshot]));
      const syncRunsByPlayer = ((syncResult.data || []) as unknown as EdrSyncRunRow[]).reduce<Record<string, EdrSyncRunRow[]>>((acc, row) => {
        if (!row.player_id) return acc;
        acc[row.player_id] ||= [];
        acc[row.player_id].push(row);
        return acc;
      }, {});

      const playerRows = rosterRows.map<EdrMovementPlayerRow>((roster) => {
        const team = teamById.get(roster.team_id);
        const dateRange = dateRangeByTeam.get(roster.team_id);
        const seasonStart = dateRange?.first_match_date ? new Date(`${dateRange.first_match_date}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
        const seasonEnd = dateRange?.last_match_date ? new Date(`${dateRange.last_match_date}T23:59:59`).getTime() : Date.now();
        const observations = (syncRunsByPlayer[roster.player_id] || []).filter((run) => {
          const observedAt = new Date(run.completed_at || run.started_at).getTime();
          return observedAt >= seasonStart && observedAt <= seasonEnd;
        });
        const snapshot = snapshotByTeamPlayer.get(`${roster.team_id}:${roster.player_id}`);
        const firstObservation = observations[0] || null;
        const lastObservation = observations[observations.length - 1] || null;
        const startEdr = numericEdr(snapshot?.baseline_edr ?? firstObservation?.observed_value ?? null);
        const currentEdr = numericEdr(lastObservation?.observed_value ?? roster.players?.estimated_dynamic_rating ?? null);
        const playerName = formatPlayerName({
          first_name: roster.players?.first_name || null,
          last_name: roster.players?.last_name || null,
          sex: roster.players?.sex || null,
        }, userPreferences);
        return {
          key: `${roster.team_id}:${roster.player_id}`,
          teamId: roster.team_id,
          teamName: team?.team_name || lookupLabel(lookups, 'teams', roster.team_id),
          seasonLabel: team?.season_label || '',
          playerId: roster.player_id,
          playerName,
          sex: roster.players?.sex || null,
          startEdr,
          currentEdr,
          delta: edrDelta(startEdr, currentEdr),
          startSource: snapshot ? 'Season baseline' : firstObservation ? 'First sync observation' : 'No baseline captured',
          startDate: snapshot?.captured_at || firstObservation?.completed_at || firstObservation?.started_at || null,
          currentDate: lastObservation?.completed_at || lastObservation?.started_at || null,
          observations,
        };
      }).sort((a, b) => a.teamName.localeCompare(b.teamName) || comparePlayerNames({ first_name: a.playerName, last_name: '' }, { first_name: b.playerName, last_name: '' }, userPreferences));

      const teamRows = Array.from(new Map(playerRows.map((row) => [row.teamId, row.teamName])).entries()).map<EdrTeamSummaryRow>(([teamId, teamName]) => {
        const rows = playerRows.filter((row) => row.teamId === teamId);
        const averageStart = averageRating(rows.map((row) => row.startEdr));
        const averageCurrent = averageRating(rows.map((row) => row.currentEdr));
        return {
          teamId,
          teamName,
          seasonLabel: rows[0]?.seasonLabel || '',
          players: rows.length,
          ratedStart: rows.filter((row) => row.startEdr !== null).length,
          ratedCurrent: rows.filter((row) => row.currentEdr !== null).length,
          averageStart,
          averageCurrent,
          averageDelta: edrDelta(averageStart, averageCurrent),
        };
      });

      const observationRows = playerRows.flatMap((row) => row.observations.map((observation, index, observations) => {
        const previous = index > 0 ? numericEdr(observations[index - 1].observed_value) : numericEdr(observation.prior_value);
        const current = numericEdr(observation.observed_value);
        return {
          ...row,
          observation,
          observedDelta: edrDelta(previous, current),
        };
      })).sort((a, b) => new Date(b.observation.completed_at || b.observation.started_at).getTime() - new Date(a.observation.completed_at || a.observation.started_at).getTime());

      setEdrPlayerRows(playerRows);
      setEdrTeamRows(teamRows);
      setEdrObservationRows(observationRows);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load EDR movement report.');
    } finally {
      setLoading(false);
    }
  };

  const captureEdrSeasonBaselines = async () => {
    if (edrTeamIds.length === 0) {
      setError('Select at least one team before capturing baselines.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const teams = await selectedEdrTeams();
      const teamById = new Map(teams.map((team) => [team.id, team]));
      const { data, error: rosterError } = await supabase
        .from('team_rosters')
        .select('team_id, player_id, players!inner(id, first_name, last_name, estimated_dynamic_rating, tennis_record_profile_url)')
        .in('team_id', edrTeamIds)
        .eq('is_active', true)
        .is('deleted_at', null)
        .eq('players.is_active', true)
        .is('players.deleted_at', null);
      if (rosterError) throw new Error(rosterError.message);
      const rows: EdrSnapshotUpsertRow[] = ((data || []) as unknown as EdrRosterRow[])
        .flatMap((roster) => {
          const team = teamById.get(roster.team_id);
          if (!team?.workspace_id) return [];
          return [{
            workspace_id: team.workspace_id,
            team_id: roster.team_id,
            player_id: roster.player_id,
            snapshot_type: 'season_start',
            snapshot_label: 'Season start baseline',
            baseline_edr: numericEdr(roster.players?.estimated_dynamic_rating),
            captured_at: new Date().toISOString(),
            source: 'current_player_record',
            tennis_record_profile_url: roster.players?.tennis_record_profile_url || null,
          }];
        });
      if (rows.length === 0) throw new Error('No active rostered players were found for the selected teams.');
      const { error: upsertError } = await supabase
        .from('player_edr_season_snapshots')
        .upsert(rows, { onConflict: 'team_id,player_id,snapshot_type' });
      if (upsertError) throw new Error(upsertError.message);
      setSnackMessage(`Captured ${rows.length} EDR season baseline${rows.length === 1 ? '' : 's'}.`);
      await loadEdrMovementReport();
    } catch (captureError) {
      setError(captureError instanceof Error ? captureError.message : 'Unable to capture EDR baselines.');
    } finally {
      setLoading(false);
    }
  };

  const copyEdrMovementReport = async () => {
    const lines = [
      `${edrTeamName} EDR Movement`,
      '',
      'Team Averages:',
      ...edrTeamRows.map((row) => `${row.teamName}: ${formatEdr(row.averageStart)} → ${formatEdr(row.averageCurrent)} (${formatDelta(row.averageDelta)})`),
      '',
      'Players:',
      ...edrPlayerRows.map((row) => `${row.teamName} - ${row.playerName}: ${formatEdr(row.startEdr)} → ${formatEdr(row.currentEdr)} (${formatDelta(row.delta)}) • ${row.startSource}`),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setSnackMessage('EDR movement report copied.');
    } catch {
      setManualCopyText(lines.join('\n'));
    }
  };

  const reportHasGeneratedContent = () => {
    if (reportType === 'player_schedule') return Boolean(playerId && hasMatchGroupSelected);
    if (reportType === 'player_participation') return participationRows.length > 0;
    if (reportType === 'player_results') return playerResultRows.length > 0;
    if (reportType === 'partner_results') return partnerRows.length > 0;
    if (reportType === 'partner_compatibility') return compatibilityRows.length > 0;
    if (reportType === 'team_schedule') return teamScheduleRows.length > 0;
    if (reportType === 'team_contacts') return contactRows.length > 0;
    if (reportType === 'team_results') return teamResultRows.length > 0;
    return edrTeamRows.length > 0 || edrPlayerRows.length > 0;
  };

  const reportPdfContext = () => {
    if (reportType === 'player_schedule') return selectedPlayerName || 'Player';
    if (reportType === 'player_participation') return participationTeamName || 'Selected Teams';
    if (reportType === 'player_results') return resultPlayerName || 'Player';
    if (reportType === 'partner_results') return partnerPlayerId ? lookupLabel(lookups, 'players', partnerPlayerId) : teamSelectionLabel(partnerTeamIds, 'All Teams');
    if (reportType === 'partner_compatibility') return teamSelectionLabel(compatibilityTeamIds, 'Selected Teams');
    if (reportType === 'team_schedule') return teamScheduleTeamName || 'Selected Teams';
    if (reportType === 'team_contacts') return contactReportTitle();
    if (reportType === 'team_results') return teamResultsTeamName || 'All Teams';
    return edrTeamName || 'Selected Teams';
  };

  const exportTeamSchedulePdf = async () => {
    if (teamScheduleRows.length === 0) {
      setSnackMessage('Run a team schedule report before exporting PDF.');
      return;
    }
    const title = reportTypeLabel('team_schedule');
    const context = reportPdfContext();
    const pdf = await PDFDocument.create();
    const regular = await pdf.embedFont(StandardFonts.Helvetica);
    const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
    const pageWidth = 612;
    const pageHeight = 792;
    const margin = 44;
    const brand = rgb(0.04, 0.35, 0.09);
    const muted = rgb(0.36, 0.36, 0.36);
    const textColor = rgb(0.12, 0.12, 0.12);
    const border = rgb(0.78, 0.82, 0.78);
    const lineHeight = 12;
    const maxWidth = pageWidth - margin * 2;
    const generatedAt = new Date().toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

    let page = pdf.addPage([pageWidth, pageHeight]);
    let y = pageHeight - margin;

    const drawText = (value: string, x: number, drawY: number, size: number, font = regular, color = textColor) => {
      page.drawText(normalizeReportPdfText(value), { x, y: drawY, size, font, color });
    };
    const wrapLine = (value: string, size: number, font = regular, width = maxWidth): string[] => {
      const words = normalizeReportPdfText(value).split(/\s+/).filter(Boolean);
      if (words.length === 0) return [''];
      const lines: string[] = [];
      let current = '';
      words.forEach((word) => {
        const next = current ? `${current} ${word}` : word;
        if (font.widthOfTextAtSize(next, size) <= width) {
          current = next;
          return;
        }
        if (current) lines.push(current);
        current = word;
      });
      if (current) lines.push(current);
      return lines;
    };
    const addPage = () => {
      page = pdf.addPage([pageWidth, pageHeight]);
      y = pageHeight - margin;
    };
    const addPageIfNeeded = (heightNeeded: number) => {
      if (y - heightNeeded >= margin) return;
      addPage();
    };
    const drawReportHeader = () => {
      drawText('CourtCaptain', margin, y, 22, bold, brand);
      y -= 24;
      drawText(title, margin, y, 15, bold, textColor);
      y -= 17;
      drawText([context, `Generated ${generatedAt}`].filter(Boolean).join(' - '), margin, y, 9, regular, muted);
      y -= 14;
      page.drawLine({ start: { x: margin, y }, end: { x: pageWidth - margin, y }, thickness: 1.2, color: brand });
      y -= 18;
    };
    const matchPdfLines = (match: TeamScheduleRow) => {
      const location = lookupLabel(lookups, 'facilities', match.facility_id) || 'Location not set';
      const time = formatTime(match.match_time, timeFormat, match) || 'Time not set';
      return {
        heading: match.opponent_name || 'TBD Opponent',
        meta: `${formatDate(localEventDate(match))} - ${time} - ${location}`,
        lines: match.lines.length > 0
          ? match.lines.map((line) => `${line.position.position_code}: ${teamScheduleLineDisplay(line, match.match_status === 'Completed')}`)
          : ['No configured lines found.'],
      };
    };
    const blockHeight = (match: TeamScheduleRow) => {
      const block = matchPdfLines(match);
      const headingLines = wrapLine(block.heading, 12, bold).length;
      const metaLines = wrapLine(block.meta, 9, regular).length;
      const lineupLines = block.lines.reduce((sum, line) => sum + wrapLine(line, 9, regular, maxWidth - 12).length, 0);
      return 18 + headingLines * 14 + metaLines * 11 + lineupLines * lineHeight + 10;
    };
    const drawMatchBlock = (match: TeamScheduleRow) => {
      const block = matchPdfLines(match);
      addPageIfNeeded(blockHeight(match));
      const top = y + 4;
      wrapLine(block.heading, 12, bold).forEach((line) => {
        drawText(line, margin, y, 12, bold, textColor);
        y -= 14;
      });
      wrapLine(block.meta, 9, regular).forEach((line) => {
        drawText(line, margin, y, 9, regular, muted);
        y -= 11;
      });
      y -= 4;
      block.lines.forEach((line) => {
        wrapLine(line, 9, regular, maxWidth - 12).forEach((wrappedLine) => {
          drawText(wrappedLine, margin + 12, y, 9, regular, textColor);
          y -= lineHeight;
        });
      });
      y -= 10;
      page.drawLine({ start: { x: margin, y: top - 6 }, end: { x: pageWidth - margin, y: top - 6 }, thickness: 0.4, color: border });
    };
    const drawSummary = () => {
      addPageIfNeeded(34 + teamSchedulePlayerSummaryRows.length * lineHeight);
      drawText('Player Schedule Summary', margin, y, 12, bold, textColor);
      y -= 16;
      teamSchedulePlayerSummaryRows.forEach((row) => {
        addPageIfNeeded(lineHeight);
        drawText(`${row.playerName}: ${row.scheduled} scheduled, ${row.played} played`, margin, y, 9, regular, textColor);
        y -= lineHeight;
      });
    };

    drawReportHeader();
    teamScheduleRows.forEach(drawMatchBlock);
    if (teamSchedulePlayerSummaryRows.length > 0) drawSummary();

    const fileName = reportPdfFilename(title, context);
    pdf.setTitle(fileName);
    pdf.setSubject(`CourtCaptain ${title}`);
    const bytes = await pdf.save();
    const arrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    downloadBlob(new Blob([arrayBuffer], { type: 'application/pdf' }), fileName);
    setSnackMessage('Report PDF generated.');
  };

  const exportReportPdf = async () => {
    if (reportType === 'team_schedule') {
      await exportTeamSchedulePdf();
      return;
    }
    const rawText = reportContentRef.current?.innerText || '';
    if (!rawText.trim() || !reportHasGeneratedContent()) {
      setSnackMessage('Run a report before exporting PDF.');
      return;
    }
    const title = reportTypeLabel(reportType);
    const context = reportPdfContext();
    const pdf = await PDFDocument.create();
    const regular = await pdf.embedFont(StandardFonts.Helvetica);
    const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
    const pageWidth = 612;
    const pageHeight = 792;
    const margin = 44;
    const brand = rgb(0.04, 0.35, 0.09);
    const muted = rgb(0.36, 0.36, 0.36);
    const textColor = rgb(0.12, 0.12, 0.12);
    const lineHeight = 13;
    const maxWidth = pageWidth - margin * 2;
    const generatedAt = new Date().toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

    let page = pdf.addPage([pageWidth, pageHeight]);
    let y = pageHeight - margin;

    const drawText = (value: string, x: number, drawY: number, size: number, font = regular, color = textColor) => {
      page.drawText(normalizeReportPdfText(value), { x, y: drawY, size, font, color });
    };
    const wrapLine = (value: string, size: number, font = regular): string[] => {
      const words = normalizeReportPdfText(value).split(/\s+/).filter(Boolean);
      if (words.length === 0) return [''];
      const lines: string[] = [];
      let current = '';
      words.forEach((word) => {
        const next = current ? `${current} ${word}` : word;
        if (font.widthOfTextAtSize(next, size) <= maxWidth) {
          current = next;
          return;
        }
        if (current) lines.push(current);
        current = word;
      });
      if (current) lines.push(current);
      return lines;
    };
    const addPageIfNeeded = (heightNeeded: number) => {
      if (y - heightNeeded >= margin) return;
      page = pdf.addPage([pageWidth, pageHeight]);
      y = pageHeight - margin;
    };

    drawText('CourtCaptain', margin, y, 22, bold, brand);
    y -= 24;
    drawText(title, margin, y, 15, bold, textColor);
    y -= 17;
    drawText([context, `Generated ${generatedAt}`].filter(Boolean).join(' - '), margin, y, 9, regular, muted);
    y -= 14;
    page.drawLine({ start: { x: margin, y }, end: { x: pageWidth - margin, y }, thickness: 1.2, color: brand });
    y -= 18;

    normalizeReportPdfText(rawText).split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        y -= lineHeight * 0.6;
        return;
      }
      const isHeading = trimmed.length < 80 && !trimmed.includes(':') && !/^\d/.test(trimmed);
      const font = isHeading ? bold : regular;
      const size = isHeading ? 11 : 9;
      wrapLine(trimmed, size, font).forEach((wrappedLine) => {
        addPageIfNeeded(lineHeight);
        drawText(wrappedLine, margin, y, size, font, textColor);
        y -= lineHeight;
      });
    });

    const fileName = reportPdfFilename(title, context);
    pdf.setTitle(fileName);
    pdf.setSubject(`CourtCaptain ${title}`);
    const bytes = await pdf.save();
    const arrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    downloadBlob(new Blob([arrayBuffer], { type: 'application/pdf' }), fileName);
    setSnackMessage('Report PDF generated.');
  };

  const toggleParticipationSort = (field: ParticipationSortField) => {
    setParticipationSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleResultSort = (field: ResultSortField) => {
    setResultSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const togglePartnerSort = (field: PartnerSortField) => {
    setPartnerSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleCompatibilitySort = (field: PartnerCompatibilitySortField) => {
    setCompatibilitySort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleContactSort = (field: ContactSortField) => {
    setContactSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const ParticipationSortHeader = ({
    field,
    label,
    align = 'left',
    rowSpan,
    sx,
    tooltip,
  }: {
    field: ParticipationSortField;
    label: string;
    align?: 'left' | 'right';
    rowSpan?: number;
    sx?: SxProps<Theme>;
    tooltip?: string;
  }) => (
    <TableCell align={align} rowSpan={rowSpan} sx={[{ fontWeight: 700 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      <TableSortLabel
        active={participationSort.field === field}
        direction={participationSort.field === field ? participationSort.direction : 'asc'}
        onClick={() => toggleParticipationSort(field)}
      >
        {tooltip ? (
          <Tooltip title={tooltip}>
            <Box component="span">{label}</Box>
          </Tooltip>
        ) : label}
      </TableSortLabel>
    </TableCell>
  );

  const ResultSortHeader = ({ field, label }: { field: ResultSortField; label: string }) => (
    <TableCell sx={{ fontWeight: 700 }}>
      <TableSortLabel active={resultSort.field === field} direction={resultSort.field === field ? resultSort.direction : 'asc'} onClick={() => toggleResultSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const PartnerSortHeader = ({ field, label, align = 'left' }: { field: PartnerSortField; label: string; align?: 'left' | 'right' }) => (
    <TableCell align={align} sx={{ fontWeight: 700 }}>
      <TableSortLabel active={partnerSort.field === field} direction={partnerSort.field === field ? partnerSort.direction : 'asc'} onClick={() => togglePartnerSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const PartnerCompatibilitySortHeader = ({ field, label }: { field: PartnerCompatibilitySortField; label: string }) => (
    <TableCell sx={{ fontWeight: 700 }}>
      <TableSortLabel active={compatibilitySort.field === field} direction={compatibilitySort.field === field ? compatibilitySort.direction : 'asc'} onClick={() => toggleCompatibilitySort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const ContactSortHeader = ({ field, label }: { field: ContactSortField; label: string }) => (
    <TableCell sx={{ fontWeight: 700 }}>
      <TableSortLabel active={contactSort.field === field} direction={contactSort.field === field ? contactSort.direction : 'asc'} onClick={() => toggleContactSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const renderItem = (item: ScheduleItem) => (
    <Paper key={`${item.match.id}:${item.position?.id || 'standby'}`} variant="outlined" sx={{ p: 1.5, borderLeft: `6px solid ${lookupTeamAccentColor(lookups, item.match.team_id)}` }}>
      <Stack spacing={0.5}>
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {matchHeading(item.match, lookups, timeFormat)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {matchDetailLine(item.match, lookups)}
        </Typography>
        <Typography variant="body2">{itemLine(item, playersById, userPreferences)}</Typography>
        {!item.isStandby && item.position && (
          <Typography variant="caption" color="text.secondary">
            {lineTypeLabel(item.position)}
          </Typography>
        )}
        {item.lineResult && (
          <Typography variant="body2">
            Result: {resultLabel(item.lineResult.line_result)}
            {item.lineResult.set_scores ? ` ${item.lineResult.set_scores}` : ''}
          </Typography>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={2}>
      {showMissingCaptainReportScope && <Alert severity="info">You aren't assigned as captain or co-captain of a team. Select a team to run this report.</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {reportTypeLabel(reportType)}
          </Typography>
        </Box>
        {reportType !== 'user_login_activity' && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={loading || !reportHasGeneratedContent()}
            onClick={() => void exportReportPdf()}
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={loading || (reportType === 'player_schedule' ? !playerId || !hasMatchGroupSelected : reportType === 'player_participation' ? participationTeamIds.length === 0 : reportType === 'player_results' ? !resultPlayerId : reportType === 'partner_compatibility' ? compatibilityTeamIds.length === 0 : reportType === 'team_schedule' ? teamScheduleTeamIds.length === 0 : reportType === 'team_contacts' ? visibleContactTeamIds().length === 0 : reportType === 'edr_movement' ? edrTeamIds.length === 0 : false)}
            onClick={() => void (reportType === 'player_schedule' ? loadReport() : reportType === 'player_participation' ? loadParticipationReport() : reportType === 'player_results' ? loadPlayerResultsReport() : reportType === 'partner_results' ? loadPartnerResultsReport() : reportType === 'partner_compatibility' ? loadPartnerCompatibilityReport() : reportType === 'team_schedule' ? loadTeamScheduleReport() : reportType === 'team_contacts' ? loadTeamContactReport() : reportType === 'team_results' ? loadTeamResultsReport() : loadEdrMovementReport())}
          >
            Run Report
          </Button>
        </Stack>}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, maxWidth: 1120 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant={reportType === 'player_schedule' ? 'contained' : 'outlined'} onClick={() => setReportType('player_schedule')}>
            Player Schedule Report
          </Button>
          <Button variant={reportType === 'player_participation' ? 'contained' : 'outlined'} onClick={() => setReportType('player_participation')}>
            Player Participation Report
          </Button>
          <Button variant={reportType === 'player_results' ? 'contained' : 'outlined'} onClick={() => setReportType('player_results')}>
            Player Results Report
          </Button>
          <Button variant={reportType === 'partner_results' ? 'contained' : 'outlined'} onClick={() => setReportType('partner_results')}>
            Partner Results Report
          </Button>
          {canViewPartnerPreferences && (
            <Button variant={reportType === 'partner_compatibility' ? 'contained' : 'outlined'} onClick={() => setReportType('partner_compatibility')}>
              Partner Compatibility Report
            </Button>
          )}
          <Button variant={reportType === 'team_schedule' ? 'contained' : 'outlined'} onClick={() => setReportType('team_schedule')}>
            Team Schedule Report
          </Button>
          <Button variant={reportType === 'team_contacts' ? 'contained' : 'outlined'} onClick={() => setReportType('team_contacts')}>
            Team Contact Report
          </Button>
          <Button variant={reportType === 'team_results' ? 'contained' : 'outlined'} onClick={() => setReportType('team_results')}>
            Team Results Report
          </Button>
          <Button variant={reportType === 'edr_movement' ? 'contained' : 'outlined'} onClick={() => setReportType('edr_movement')}>
            EDR Movement Report
          </Button>
          {canViewLoginActivity && <Button variant={reportType === 'user_login_activity' ? 'contained' : 'outlined'} onClick={() => setReportType('user_login_activity')}>
            User Login Activity
          </Button>}
        </Stack>
      </Paper>

      {reportType === 'user_login_activity' && <UserLoginActivityReport lookups={lookups} authorizedTeamIds={defaultCaptainTeamIds} canViewAllTeams={canViewLoginActivity} />}

      {reportType === 'player_schedule' && (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Player</InputLabel>
              <Select label="Player" value={playerId} onChange={(event) => setPlayerId(event.target.value)}>
                {lookups.players.map((player) => (
                  <MenuItem key={player.value} value={player.value}>
                    {player.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <SeasonTeamMultiSelect lookups={lookups} value={teamIds} onChange={(ids) => { setTeamIds(ids); markReportTeamSelection('player_schedule'); }} allLabel={isUnselectedCaptainReport('player_schedule', teamIds) ? 'Select Teams' : 'All Teams'} />
          </Box>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControlLabel control={<Checkbox checked={includeCompleted} onChange={(event) => setIncludeCompleted(event.target.checked)} />} label="Include Completed Matches" />
            <FormControlLabel control={<Checkbox checked={includeFuture} onChange={(event) => setIncludeFuture(event.target.checked)} />} label="Include Future Matches" />
          </Stack>
        </Stack>
      </Paper>
      )}

      {reportType === 'player_participation' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 520px)' }, gap: 2 }}>
              <SeasonTeamMultiSelect lookups={lookups} value={participationTeamIds} onChange={(ids) => { setParticipationTeamIds(ids); markReportTeamSelection('player_participation'); }} allLabel="Select Teams" />
            </Box>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <FormControlLabel control={<Checkbox checked={includeInactivePlayers} onChange={(event) => setIncludeInactivePlayers(event.target.checked)} />} label="Include Inactive Players" />
              <FormControlLabel control={<Checkbox checked={includeSubstitutePlayers} onChange={(event) => setIncludeSubstitutePlayers(event.target.checked)} />} label="Include Substitute Players" />
            </Stack>
          </Stack>
        </Paper>
      )}

      {reportType === 'player_results' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Player</InputLabel>
                <Select label="Player" value={resultPlayerId} onChange={(event) => setResultPlayerId(event.target.value)}>
                  {lookups.players.map((player) => <MenuItem key={player.value} value={player.value}>{player.label}</MenuItem>)}
                </Select>
              </FormControl>
              <SeasonTeamMultiSelect lookups={lookups} value={resultTeamIds} onChange={(ids) => { setResultTeamIds(ids); markReportTeamSelection('player_results'); }} allLabel={isUnselectedCaptainReport('player_results', resultTeamIds) ? 'Select Teams' : 'All Teams'} />
              <TextField label="Date From" type="date" value={resultDateFrom} onChange={(event) => setResultDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label="Date To" type="date" value={resultDateTo} onChange={(event) => setResultDateTo(event.target.value)} InputLabelProps={{ shrink: true }} />
              <FormControl fullWidth>
                <InputLabel>Line Type</InputLabel>
                <Select label="Line Type" value={resultLineType} onChange={(event) => setResultLineType(event.target.value)}>
                  <MenuItem value="">All Line Types</MenuItem>
                  <MenuItem value="singles">Singles</MenuItem>
                  <MenuItem value="doubles">Doubles</MenuItem>
                  <MenuItem value="mixed_doubles">Mixed Doubles</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Match Site Type</InputLabel>
                <Select label="Match Site Type" value={resultSiteType} onChange={(event) => setResultSiteType(event.target.value)}>
                  <MenuItem value="">All Site Types</MenuItem>
                  <MenuItem value="Home">Home</MenuItem>
                  <MenuItem value="Away">Away</MenuItem>
                  <MenuItem value="Neutral">Neutral</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Paper>
      )}

      {reportType === 'partner_results' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <SeasonTeamMultiSelect lookups={lookups} value={partnerTeamIds} onChange={(ids) => { setPartnerTeamIds(ids); markReportTeamSelection('partner_results'); }} allLabel={isUnselectedCaptainReport('partner_results', partnerTeamIds) ? 'Select Teams' : 'All Teams'} />
              <FormControl fullWidth>
                <InputLabel>Player</InputLabel>
                <Select label="Player" value={partnerPlayerId} onChange={(event) => setPartnerPlayerId(event.target.value)}>
                  <MenuItem value="">All Players</MenuItem>
                  {lookups.players.map((player) => <MenuItem key={player.value} value={player.value}>{player.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Date From" type="date" value={partnerDateFrom} onChange={(event) => setPartnerDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label="Date To" type="date" value={partnerDateTo} onChange={(event) => setPartnerDateTo(event.target.value)} InputLabelProps={{ shrink: true }} />
              <FormControl fullWidth>
                <InputLabel>Match Site Type</InputLabel>
                <Select label="Match Site Type" value={partnerSiteType} onChange={(event) => setPartnerSiteType(event.target.value)}>
                  <MenuItem value="">All Site Types</MenuItem>
                  <MenuItem value="Home">Home</MenuItem>
                  <MenuItem value="Away">Away</MenuItem>
                  <MenuItem value="Neutral">Neutral</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Minimum Matches Played" type="number" value={partnerMinimumMatches} onChange={(event) => setPartnerMinimumMatches(event.target.value)} inputProps={{ min: 1 }} />
            </Box>
          </Stack>
        </Paper>
      )}

      {reportType === 'partner_compatibility' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 520px)' }, gap: 2 }}>
              <SeasonTeamMultiSelect lookups={lookups} value={compatibilityTeamIds} onChange={(ids) => { setCompatibilityTeamIds(ids); markReportTeamSelection('partner_compatibility'); }} allLabel="Select Teams" />
            </Box>
          </Stack>
        </Paper>
      )}

      {reportType === 'team_schedule' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 520px) repeat(2, 1fr)' }, gap: 2 }}>
              <SeasonTeamMultiSelect lookups={lookups} value={teamScheduleTeamIds} onChange={(ids) => { setTeamScheduleTeamIds(ids); markReportTeamSelection('team_schedule'); }} allLabel="Select Teams" />
              <TextField label="Date From" type="date" value={teamScheduleDateFrom} onChange={(event) => setTeamScheduleDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label="Date To" type="date" value={teamScheduleDateTo} onChange={(event) => setTeamScheduleDateTo(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
          </Stack>
        </Paper>
      )}

      {reportType === 'team_contacts' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 520px)' }, gap: 2 }}>
              <SeasonTeamMultiSelect
                lookups={lookups}
                value={contactTeamIds}
                allLabel="Select Teams"
                onChange={(teamIds) => {
                  setContactTeamIds(teamIds);
                  markReportTeamSelection('team_contacts');
                  setContactRows([]);
                }}
              />
            </Box>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <FormControlLabel control={<Checkbox checked={contactIncludePhone} onChange={(event) => setContactIncludePhone(event.target.checked)} />} label="Include Phone Number" />
              <FormControlLabel control={<Checkbox checked={contactIncludeEmail} onChange={(event) => setContactIncludeEmail(event.target.checked)} />} label="Include Email" />
              <FormControlLabel control={<Checkbox checked={contactIncludeCityState} onChange={(event) => setContactIncludeCityState(event.target.checked)} />} label="Include City/State" />
              <FormControlLabel control={<Checkbox checked={contactDeduplicate} onChange={(event) => setContactDeduplicate(event.target.checked)} />} label="Deduplicate Players" />
            </Stack>
          </Stack>
        </Paper>
      )}

      {reportType === 'team_results' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <SeasonTeamMultiSelect lookups={lookups} value={teamResultsTeamIds} onChange={(ids) => { setTeamResultsTeamIds(ids); markReportTeamSelection('team_results'); }} allLabel={isUnselectedCaptainReport('team_results', teamResultsTeamIds) ? 'Select Teams' : 'All Teams'} />
              <FormControl fullWidth>
                <InputLabel>Match Status</InputLabel>
                <Select value={teamResultsMatchStatus} label="Match Status" onChange={(event) => setTeamResultsMatchStatus(event.target.value)}>
                  <MenuItem value="">All Match Statuses</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Published">Published</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Result Status</InputLabel>
                <Select value={teamResultsResultStatus} label="Result Status" onChange={(event) => setTeamResultsResultStatus(event.target.value)}>
                  <MenuItem value="">All Result Statuses</MenuItem>
                  <MenuItem value="Not Started">Not Started</MenuItem>
                  <MenuItem value="Draft">Draft</MenuItem>
                  <MenuItem value="Confirmed">Confirmed</MenuItem>
                  <MenuItem value="Submitted">Submitted</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Date From" type="date" value={teamResultsDateFrom} onChange={(event) => setTeamResultsDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label="Date To" type="date" value={teamResultsDateTo} onChange={(event) => setTeamResultsDateTo(event.target.value)} InputLabelProps={{ shrink: true }} />
              <FormControl fullWidth>
                <InputLabel>Match Site Type</InputLabel>
                <Select value={teamResultsSiteType} label="Match Site Type" onChange={(event) => setTeamResultsSiteType(event.target.value)}>
                  <MenuItem value="">All Site Types</MenuItem>
                  <MenuItem value="Home">Home</MenuItem>
                  <MenuItem value="Away">Away</MenuItem>
                  <MenuItem value="Neutral">Neutral</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Team Result</InputLabel>
                <Select value={teamResultsResultValue} label="Team Result" onChange={(event) => setTeamResultsResultValue(event.target.value)}>
                  <MenuItem value="">All Team Results</MenuItem>
                  <MenuItem value="Win">Win</MenuItem>
                  <MenuItem value="Loss">Loss</MenuItem>
                  <MenuItem value="Tie">Tie</MenuItem>
                  <MenuItem value="Unknown">Unknown</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Paper>
      )}

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {reportType === 'player_schedule' && !hasMatchGroupSelected && <Alert severity="warning">Select at least one match group to include.</Alert>}

      <Box ref={reportContentRef} sx={{ display: 'contents' }}>
      {reportType === 'player_schedule' && (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Player Schedule Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {playerId ? selectedPlayerName : 'Select a player to run the report.'}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={!playerId || loading || !hasMatchGroupSelected} onClick={() => void copySchedule()}>
            Copy Player Schedule
          </Button>
        </Stack>
      </Paper>
      )}

      {reportType === 'player_schedule' && hasMatchGroupSelected && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: includeCompleted && includeFuture ? 'repeat(2, 1fr)' : '1fr' }, gap: 2 }}>
          {includeCompleted && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Completed Matches
              </Typography>
              <Stack spacing={1}>{visibleCompletedItems.length > 0 ? visibleCompletedItems.map(renderItem) : <Typography variant="body2" color="text.secondary">No completed matches found.</Typography>}</Stack>
            </Paper>
          )}

          {includeFuture && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Future Scheduled Matches
              </Typography>
              <Stack spacing={1}>{visibleFutureItems.length > 0 ? visibleFutureItems.map(renderItem) : <Typography variant="body2" color="text.secondary">No future matches found.</Typography>}</Stack>
            </Paper>
          )}
        </Box>
      )}

      {reportType === 'player_participation' && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Player Participation Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {participationTeamIds.length > 0 ? participationTeamName : 'Select one or more teams to run the report.'}
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={participationTeamIds.length === 0 || loading} onClick={() => void copyParticipationReport()}>
                Copy Participation Report
              </Button>
            </Stack>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(8, 1fr)' }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Roster Size</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{participationSummary.rosterSize}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Original Target</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{participationSummary.totalTarget}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Tooltip title="Original Target minus Late Cancels, plus additional opportunities earned through Late Substitute appearances.">
                <Typography component="span" variant="caption" color="text.secondary">Adjusted Target</Typography>
              </Tooltip>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{participationSummary.totalAdjustedTarget}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Actual Played</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{participationSummary.totalPlayed}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Late Cancels</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{participationSummary.totalLateCancels}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Late Substitutes</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{participationSummary.totalLateSubstitutes}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Future Regular</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{participationSummary.totalFuture}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Average Target Status</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{participationSummary.averageStatus.toFixed(1)}</Typography>
            </Paper>
          </Box>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <ParticipationSortHeader field="playerName" label="Player" />
                    <ParticipationSortHeader field="targetMatches" label="Original Target" align="right" />
                    <ParticipationSortHeader
                      field="adjustedTarget"
                      label="Adjusted Target"
                      align="right"
                      tooltip="Original Target minus Late Cancels, plus additional opportunities earned through Late Substitute appearances."
                    />
                    <ParticipationSortHeader field="lateCancels" label="Late Cancels" align="right" />
                    <ParticipationSortHeader
                      field="lateSubstitutes"
                      label="Late Substitutes"
                      align="right"
                      tooltip="Matches played as a replacement for a late cancellation. These appearances increase Adjusted Target and do not count against the player's regular allocation."
                    />
                    <ParticipationSortHeader field="totalPlayed" label="Played" align="right" />
                    <ParticipationSortHeader field="totalFuture" label="Future" align="right" />
                    <ParticipationSortHeader field="statusDelta" label="Status" align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedParticipationRows.map((row, index) => (
                    <TableRow key={`${row.playerId}:${index}`} hover onClick={() => setSelectedParticipationRow(row)} sx={{ cursor: 'pointer' }}>
                      <TableCell>{row.playerName}</TableCell>
                      <TableCell align="right">{row.targetMatches}</TableCell>
                      <TableCell align="right">{row.adjustedTarget}</TableCell>
                      <TableCell align="right">
                        <Typography component="span" color="error.main" sx={{ fontWeight: row.lateCancels > 0 ? 700 : 400 }}>
                          {row.lateCancels}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography component="span" color="success.main" sx={{ fontWeight: row.lateSubstitutes > 0 ? 700 : 400 }}>
                          {row.lateSubstitutes}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{row.totalPlayed} (S:{row.singlesPlayed} / D:{row.doublesPlayed})</TableCell>
                      <TableCell align="right">{row.totalFuture} (S:{row.futureSingles} / D:{row.futureDoubles})</TableCell>
                      <TableCell align="right">
                        <Typography component="span" color={`${statusColor(row.statusDelta)}.main`} sx={{ fontWeight: 700 }}>
                          {statusLabel(row.statusDelta)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sortedParticipationRows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                          No rostered players match the current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      {reportType === 'player_results' && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Player Results Report</Typography>
                <Typography variant="body2" color="text.secondary">{resultPlayerId ? resultPlayerName : 'Select a player to run the report.'}</Typography>
              </Box>
              <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={!resultPlayerId || loading || playerResultRows.length === 0} onClick={() => void copyPlayerResultsReport()}>
                Copy Player Results
              </Button>
            </Stack>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
            {[
              ['Overall', recordText(playerResultSummary.wins, playerResultSummary.losses)],
              ['Singles', recordText(playerResultSummary.singlesWins, playerResultSummary.singlesLosses)],
              ['Doubles', recordText(playerResultSummary.doublesWins, playerResultSummary.doublesLosses)],
              ['Mixed Doubles', recordText(playerResultSummary.mixedWins, playerResultSummary.mixedLosses)],
              ['Home', recordText(playerResultSummary.homeWins, playerResultSummary.homeLosses)],
              ['Away', recordText(playerResultSummary.awayWins, playerResultSummary.awayLosses)],
              ['Neutral', recordText(playerResultSummary.neutralWins, playerResultSummary.neutralLosses)],
              ['Matches Played', String(playerResultSummary.wins + playerResultSummary.losses)],
              ['Win %', winPercentage(playerResultSummary.wins, playerResultSummary.losses)],
              ['Late Cancels', String(playerResultSummary.lateCancels)],
              ['Late Substitutes', String(playerResultSummary.lateSubstitutes)],
            ].map(([label, value]) => (
              <Paper key={label} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
              </Paper>
            ))}
          </Box>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <ResultSortHeader field="match_date" label="Match Date" />
                    <ResultSortHeader field="team" label="Team" />
                    <ResultSortHeader field="opponent" label="Opponent" />
                    <TableCell sx={{ fontWeight: 700 }}>Site</TableCell>
                    <ResultSortHeader field="position" label="Position" />
                    <TableCell sx={{ fontWeight: 700 }}>Partner</TableCell>
                    <ResultSortHeader field="line_result" label="Line Result" />
                    <ResultSortHeader field="team_result" label="Team Result" />
                    <TableCell sx={{ fontWeight: 700 }}>Result Status</TableCell>
                    <ResultSortHeader field="facility" label="Facility" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPlayerResultRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDate(localEventDate(row.match))}</TableCell>
                      <TableCell>{lookupLabel(lookups, 'teams', row.match.team_id)}</TableCell>
                      <TableCell>{row.match.opponent_name || 'TBD'}</TableCell>
                      <TableCell>{row.match.match_site_type || 'Unknown'}</TableCell>
                      <TableCell>{row.position.position_code}</TableCell>
                      <TableCell>{row.partners.length > 0 ? row.partners.join(' / ') : '-'}</TableCell>
                      <TableCell>{resultLabel(row.lineResult)}</TableCell>
                      <TableCell>{teamResultFromScores(row.match)}</TableCell>
                      <TableCell>{row.match.result_status}</TableCell>
                      <TableCell>{lookupLabel(lookups, 'facilities', row.match.facility_id)}</TableCell>
                    </TableRow>
                  ))}
                  {sortedPlayerResultRows.length === 0 && !loading && (
                    <TableRow><TableCell colSpan={10}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No completed player results found.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      {reportType === 'partner_results' && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Partner Results Report</Typography>
                <Typography variant="body2" color="text.secondary">Doubles and mixed doubles partner records.</Typography>
              </Box>
              <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={loading || partnerRows.length === 0} onClick={() => void copyPartnerResultsReport()}>
                Copy Partner Results
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <PartnerSortHeader field="player1" label="Player 1" />
                    <PartnerSortHeader field="player2" label="Player 2" />
                    <TableCell sx={{ fontWeight: 700 }}>Teams</TableCell>
                    <PartnerSortHeader field="matches" label="Matches Played" align="right" />
                    <PartnerSortHeader field="record" label="Record" />
                    <PartnerSortHeader field="win_percentage" label="Win %" />
                    <PartnerSortHeader field="last_played" label="Last Played" />
                    <TableCell sx={{ fontWeight: 700 }}>Home Record</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Away Record</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPartnerRows.map((row) => (
                    <TableRow key={row.key} hover onClick={() => setSelectedPartnerRow(row)} sx={{ cursor: 'pointer' }}>
                      <TableCell>{lookupLabel(lookups, 'players', row.player1Id)}</TableCell>
                      <TableCell>{lookupLabel(lookups, 'players', row.player2Id)}</TableCell>
                      <TableCell>{row.teams.map((team) => lookupLabel(lookups, 'teams', team)).join(', ')}</TableCell>
                      <TableCell align="right">{row.details.length}</TableCell>
                      <TableCell>{recordText(row.wins, row.losses)}</TableCell>
                      <TableCell>{winPercentage(row.wins, row.losses)}</TableCell>
                      <TableCell>{row.lastPlayed ? formatDate(row.lastPlayed) : '-'}</TableCell>
                      <TableCell>{recordText(row.homeWins, row.homeLosses)}</TableCell>
                      <TableCell>{recordText(row.awayWins, row.awayLosses)}</TableCell>
                    </TableRow>
                  ))}
                  {sortedPartnerRows.length === 0 && !loading && (
                    <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No partner results found.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      {reportType === 'partner_compatibility' && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Partner Compatibility Report</Typography>
                <Typography variant="body2" color="text.secondary">Saved workspace-wide partner compatibility preferences.</Typography>
              </Box>
              <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={loading || compatibilityRows.length === 0} onClick={() => void copyPartnerCompatibilityReport()}>
                Copy Partner Compatibility
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <PartnerCompatibilitySortHeader field="player1" label="Player 1" />
                    <PartnerCompatibilitySortHeader field="player2" label="Player 2" />
                    <TableCell sx={{ fontWeight: 700 }}>Workspace</TableCell>
                    <PartnerCompatibilitySortHeader field="compatibility" label="Compatibility" />
                    <PartnerCompatibilitySortHeader field="notes" label="Notes" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCompatibilityRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{lookupLabel(lookups, 'players', row.player1_id)}</TableCell>
                      <TableCell>{lookupLabel(lookups, 'players', row.player2_id)}</TableCell>
                      <TableCell>{lookupLabel(lookups, 'workspaces', row.workspace_id)}</TableCell>
                      <TableCell>
                        <Chip size="small" color={compatibilityChipColor(row.compatibility)} label={row.compatibility || 'Unknown'} />
                      </TableCell>
                      <TableCell>{row.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {sortedCompatibilityRows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                          No partner preferences found for the selected workspaces.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      {reportType === 'team_schedule' && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Team Schedule Report</Typography>
                <Typography variant="body2" color="text.secondary">
                  {teamScheduleTeamIds.length > 0 ? `${teamScheduleTeamName} • ${teamScheduleRows.length} matches` : 'Select one or more teams to run the report.'}
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={loading || teamScheduleRows.length === 0} onClick={() => void copyTeamScheduleReport()}>
                Copy Team Schedule
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Warmup</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Opponent</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Match Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Lineup Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Line Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamScheduleRows.map((match) => (
                    <TableRow key={match.id} hover>
                      <TableCell>{formatDate(localEventDate(match))}</TableCell>
                      <TableCell>{formatTime(match.match_time, timeFormat, match) || 'Not set'}</TableCell>
                      <TableCell>{formatTime(match.warmup_time, timeFormat, match) || 'Not set'}</TableCell>
                      <TableCell>{lookupLabel(lookups, 'teams', match.team_id)}</TableCell>
                      <TableCell>{match.opponent_name || 'TBD'}</TableCell>
                      <TableCell>{lookupLabel(lookups, 'facilities', match.facility_id) || 'Location not set'}</TableCell>
                      <TableCell>{match.match_status}</TableCell>
                      <TableCell>{match.lineup_status || 'Not Started'}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {match.lines.length > 0 ? match.lines.map((line) => (
                            <Typography key={line.position.id} variant="body2">
                              <Box component="span" sx={{ fontWeight: 700 }}>{line.position.position_code}:</Box> {teamScheduleLineDisplay(line, match.match_status === 'Completed')}
                            </Typography>
                          )) : <Typography variant="body2" color="text.secondary">No configured lines found.</Typography>}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {teamScheduleRows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                          No schedule loaded yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Player Schedule Summary</Typography>
              <Typography variant="body2" color="text.secondary">Counts reflect the selected teams and date range at the time the report is run.</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Teams</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Scheduled</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Played</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamSchedulePlayerSummaryRows.map((row) => (
                    <TableRow key={row.playerId} hover>
                      <TableCell>{row.playerName}</TableCell>
                      <TableCell>{row.teamNames.length > 0 ? row.teamNames.join(', ') : '-'}</TableCell>
                      <TableCell align="right">{row.scheduled}</TableCell>
                      <TableCell align="right">{row.played}</TableCell>
                    </TableRow>
                  ))}
                  {teamSchedulePlayerSummaryRows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                          No player schedule summary loaded yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      {reportType === 'team_contacts' && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Team Contact Report</Typography>
                <Typography variant="body2" color="text.secondary">
                  {contactTeamIds.length > 0 ? `${contactReportTitle()} • ${sortedContactRows.length} players` : 'Select one or more teams to run the report.'}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="outlined" startIcon={<ContentCopyIcon />} disabled={loading || contactRows.length === 0} onClick={() => void copyContactList()}>
                  Copy Contact List
                </Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} disabled={loading || contactRows.length === 0} onClick={exportContactCsv}>
                  Export CSV
                </Button>
                <Button variant="contained" startIcon={<OpenInNewIcon />} disabled={loading || contactRows.length === 0} onClick={() => void openContactGoogleSheets()}>
                  Open in Google Sheets
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <ContactSortHeader field="name" label="Player Name" />
                    {contactIncludePhone && <TableCell sx={{ fontWeight: 700 }}>Phone Number</TableCell>}
                    {contactIncludeEmail && <TableCell sx={{ fontWeight: 700 }}>Email Address</TableCell>}
                    {contactIncludeCityState && <ContactSortHeader field="city" label="City" />}
                    {contactIncludeCityState && <ContactSortHeader field="state" label="State" />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedContactRows.map((row) => (
                    <TableRow key={row.key} hover>
                      <TableCell>{formatPlayerName(row, userPreferences)}</TableCell>
                      {contactIncludePhone && <TableCell>{row.mobile_phone || '-'}</TableCell>}
                      {contactIncludeEmail && <TableCell>{row.email || '-'}</TableCell>}
                      {contactIncludeCityState && <TableCell>{row.city || '-'}</TableCell>}
                      {contactIncludeCityState && <TableCell>{row.state || '-'}</TableCell>}
                    </TableRow>
                  ))}
                  {sortedContactRows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={1 + (contactIncludePhone ? 1 : 0) + (contactIncludeEmail ? 1 : 0) + (contactIncludeCityState ? 2 : 0)}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                          No contacts loaded yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      {reportType === 'team_results' && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Team Results Report</Typography>
                <Typography variant="body2" color="text.secondary">
                  {teamResultsTeamIds.length > 0 ? teamResultsTeamName : 'Run the report for all teams or narrow it with filters.'}
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={loading || teamResultRows.length === 0} onClick={() => void copyTeamResultsReport()}>
                Copy Team Results Summary
              </Button>
            </Stack>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {[
              ['Overall Team Record', recordText(teamResultsSummary.wins, teamResultsSummary.losses)],
              ['Home Record', recordText(teamResultsSummary.homeWins, teamResultsSummary.homeLosses)],
              ['Away Record', recordText(teamResultsSummary.awayWins, teamResultsSummary.awayLosses)],
              ['Neutral Record', recordText(teamResultsSummary.neutralWins, teamResultsSummary.neutralLosses)],
              ['Current Streak', teamResultsSummary.streak],
              ['Total Matches', String(teamResultRows.length)],
              ['Total Lines Won', String(teamResultsSummary.linesWon)],
              ['Total Lines Lost', String(teamResultsSummary.linesLost)],
              ['Line Win %', winPercentage(teamResultsSummary.linesWon, teamResultsSummary.linesLost)],
            ].map(([label, value]) => (
              <Paper key={label} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
              </Paper>
            ))}
          </Box>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Line-Level Summary</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {Object.entries(teamResultsSummary.byLine).length > 0 ? Object.entries(teamResultsSummary.byLine).map(([line, record]) => (
                <Paper key={line} variant="outlined" sx={{ px: 1.5, py: 1, minWidth: 96 }}>
                  <Typography variant="caption" color="text.secondary">{line}</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>{recordText(record.wins, record.losses)}</Typography>
                </Paper>
              )) : <Typography variant="body2" color="text.secondary">No counted line results found.</Typography>}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Opponent</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Site</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Match Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Score</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Team Result</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Opponent Avg EDR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Lineup Avg EDR</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Line Results</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamResultRows.map((match) => (
                    <TableRow key={match.id} hover>
                      <TableCell>{formatDate(localEventDate(match))}{formatTime(match.match_time, timeFormat, match) ? ` ${formatTime(match.match_time, timeFormat, match)}` : ''}</TableCell>
                      <TableCell>{lookupLabel(lookups, 'teams', match.team_id)}</TableCell>
                      <TableCell>{match.opponent_name || 'TBD'}</TableCell>
                      <TableCell>{match.match_site_type || 'Unknown'}</TableCell>
                      <TableCell>{match.match_status}</TableCell>
                      <TableCell align="right">{match.our_team_score}-{match.opponent_team_score}</TableCell>
                      <TableCell>{teamResultFromScores(match)}</TableCell>
                      <TableCell align="right">{formatEdr(match.opponent_average_edr)}</TableCell>
                      <TableCell align="right">{formatEdr(match.lineup_average_edr)}</TableCell>
                      <TableCell>{match.lines.length > 0 ? match.lines.map((line) => `${line.position.position_code}: ${resultLabel(line.result)}`).join(', ') : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {teamResultRows.length === 0 && !loading && (
                    <TableRow><TableCell colSpan={10}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No match results found.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      {reportType === 'edr_movement' && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>EDR Movement Report</Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare beginning EDR to current/end EDR by player and team average. Captured baselines are preferred; otherwise the report uses the first TennisRecord observation in the season.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="outlined" startIcon={<HistoryIcon />} disabled={loading || edrTeamIds.length === 0} onClick={() => void captureEdrSeasonBaselines()}>
                  Capture Season Baseline
                </Button>
                <Button variant="contained" startIcon={<ContentCopyIcon />} disabled={loading || edrPlayerRows.length === 0} onClick={() => void copyEdrMovementReport()}>
                  Copy EDR Summary
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 520px)' }, gap: 2 }}>
              <SeasonTeamMultiSelect lookups={lookups} value={edrTeamIds} onChange={(ids) => { setEdrTeamIds(ids); markReportTeamSelection('edr_movement'); }} label="Teams" allLabel="Select Teams" />
            </Box>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {[
              ['Teams', String(edrTeamRows.length)],
              ['Players', String(edrPlayerRows.length)],
              ['Rated at Start', String(edrPlayerRows.filter((row) => row.startEdr !== null).length)],
              ['Rated Current', String(edrPlayerRows.filter((row) => row.currentEdr !== null).length)],
            ].map(([label, value]) => (
              <Paper key={label} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
              </Paper>
            ))}
          </Box>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Team Average EDR</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Season</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Players</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Start Rated</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Current Rated</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Start Avg EDR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Current Avg EDR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Delta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {edrTeamRows.map((row) => (
                    <TableRow key={row.teamId} hover>
                      <TableCell>{row.teamName}</TableCell>
                      <TableCell>{row.seasonLabel || '-'}</TableCell>
                      <TableCell align="right">{row.players}</TableCell>
                      <TableCell align="right">{row.ratedStart}</TableCell>
                      <TableCell align="right">{row.ratedCurrent}</TableCell>
                      <TableCell align="right">{formatEdr(row.averageStart)}</TableCell>
                      <TableCell align="right">{formatEdr(row.averageCurrent)}</TableCell>
                      <TableCell align="right">{formatDelta(row.averageDelta)}</TableCell>
                    </TableRow>
                  ))}
                  {edrTeamRows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                          Select teams and run the report to see team average EDR movement.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Player Season EDR Delta</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 460 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Start Source</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Start Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Current Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Start EDR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Current EDR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Delta</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Observations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {edrPlayerRows.map((row) => (
                    <TableRow key={row.key} hover>
                      <TableCell>{row.teamName}</TableCell>
                      <TableCell>{row.playerName}</TableCell>
                      <TableCell>{row.startSource}</TableCell>
                      <TableCell>{dateOnly(row.startDate) || '-'}</TableCell>
                      <TableCell>{dateOnly(row.currentDate) || '-'}</TableCell>
                      <TableCell align="right">{formatEdr(row.startEdr)}</TableCell>
                      <TableCell align="right">{formatEdr(row.currentEdr)}</TableCell>
                      <TableCell align="right">{formatDelta(row.delta)}</TableCell>
                      <TableCell align="right">{row.observations.length}</TableCell>
                    </TableRow>
                  ))}
                  {edrPlayerRows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                          No player EDR movement loaded yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Observed EDR Over Time</Typography>
              <Typography variant="body2" color="text.secondary">Timestamped TennisRecord observations captured during player syncs.</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Observed</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Observed EDR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Delta From Prior</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Changed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {edrObservationRows.map((row) => (
                    <TableRow key={`${row.key}:${row.observation.started_at}`} hover>
                      <TableCell>{formatDateTime(row.observation.completed_at || row.observation.started_at)}</TableCell>
                      <TableCell>{row.teamName}</TableCell>
                      <TableCell>{row.playerName}</TableCell>
                      <TableCell align="right">{formatEdr(row.observation.observed_value)}</TableCell>
                      <TableCell align="right">{formatDelta(row.observedDelta)}</TableCell>
                      <TableCell>{row.observation.changed ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                  {edrObservationRows.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                          No timestamped EDR observations found for the selected teams.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}
      </Box>

      <Snackbar open={Boolean(snackMessage)} autoHideDuration={4000} message={snackMessage || ''} onClose={() => setSnackMessage(null)} />

      <Dialog open={Boolean(selectedParticipationRow)} onClose={() => setSelectedParticipationRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedParticipationRow ? `${selectedParticipationRow.playerName} Participation Detail` : 'Participation Detail'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {selectedParticipationRow && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, 1fr)' }, gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Original Target</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>{selectedParticipationRow.targetMatches}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Tooltip title="Original Target minus Late Cancels, plus additional opportunities earned through Late Substitute appearances.">
                    <Typography component="span" variant="caption" color="text.secondary">Adjusted Target</Typography>
                  </Tooltip>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>{selectedParticipationRow.adjustedTarget}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Late Cancels</Typography>
                  <Typography variant="body1" color="error.main" sx={{ fontWeight: 700 }}>{selectedParticipationRow.lateCancels}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Tooltip title="Matches played as a replacement for a late cancellation. These appearances increase Adjusted Target and do not count against the player's regular allocation.">
                    <Typography component="span" variant="caption" color="text.secondary">Late Substitutes</Typography>
                  </Tooltip>
                  <Typography variant="body1" color="success.main" sx={{ fontWeight: 700 }}>{selectedParticipationRow.lateSubstitutes}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography color={`${statusColor(selectedParticipationRow.statusDelta)}.main`} sx={{ fontWeight: 700 }}>
                    {statusLabel(selectedParticipationRow.statusDelta)}
                  </Typography>
                </Paper>
              </Box>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Match Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Opponent</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Line Assignment</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Participation Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cancellation Reason</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Replacement Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedParticipationRow?.details || []).map((detail) => (
                    <TableRow key={detail.id}>
                      <TableCell>{detail.matchDate ? formatDate(detail.matchDate) : '-'}</TableCell>
                      <TableCell>{detail.opponent}</TableCell>
                      <TableCell>{detail.lineAssignment}</TableCell>
                      <TableCell>{detail.participationStatus}</TableCell>
                      <TableCell>{detail.cancellationReason || '-'}</TableCell>
                      <TableCell>{detail.replacementDetails || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {selectedParticipationRow && selectedParticipationRow.details.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                          No participation details found for this player.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          {selectedParticipationRow && onOpenParticipationEventsForPlayer && (
            <Button
              startIcon={<HistoryIcon />}
              onClick={() => {
                onOpenParticipationEventsForPlayer(selectedParticipationRow.playerId);
                setSelectedParticipationRow(null);
              }}
            >
              View Participation Log
            </Button>
          )}
          <Button onClick={() => setSelectedParticipationRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedPartnerRow)} onClose={() => setSelectedPartnerRow(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedPartnerRow
            ? `${lookupLabel(lookups, 'players', selectedPartnerRow.player1Id)} / ${lookupLabel(lookups, 'players', selectedPartnerRow.player2Id)}`
            : 'Partner Results'}
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Match Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Opponent</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Line Result</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Team Result</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Facility</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Match Site Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedPartnerRow?.details || []).map((detail) => (
                  <TableRow key={detail.id}>
                    <TableCell>{formatDate(localEventDate(detail.match))}</TableCell>
                    <TableCell>{lookupLabel(lookups, 'teams', detail.match.team_id)}</TableCell>
                    <TableCell>{detail.match.opponent_name || 'TBD'}</TableCell>
                    <TableCell>{detail.position.position_code}</TableCell>
                    <TableCell>{resultLabel(detail.lineResult)}</TableCell>
                    <TableCell>{teamResultFromScores(detail.match)}</TableCell>
                    <TableCell>{lookupLabel(lookups, 'facilities', detail.match.facility_id)}</TableCell>
                    <TableCell>{detail.match.match_site_type || 'Unknown'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedPartnerRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(manualCopyText)} onClose={() => setManualCopyText('')} maxWidth="md" fullWidth>
        <DialogTitle>Copy Report Text</DialogTitle>
        <DialogContent>
          <TextField value={manualCopyText} multiline minRows={12} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualCopyText('')}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
